import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Button, Col, Empty, Input, Modal, Row, Select, Spin, Switch, Table, Tag, Tooltip, message as antMsg,
} from 'antd';
import { ReloadOutlined, FilterOutlined, SyncOutlined, UploadOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';
import attritionOccupancyService from '../../services/attritionOccupancyService';
import clusterService from '../../services/clusterService';

const { Option } = Select;

// ── Colour tokens ─────────────────────────────────────────────────
const C = {
  navy:  '#1B2D6B',
  blue:  '#1E6FBF',
  green: '#16a34a',
  red:   '#dc2626',
  amber: '#d97706',
  purple:'#7c3aed',
  surf:  '#f8fafc',
  border:'#e2e8f0',
  text1: '#1a2d4a',
  text3: '#6b7280',
};

// ── Analytical KPI card ───────────────────────────────────────────
// risk: 'critical' | 'warning' | 'good' | 'info' | 'neutral'
const RISK_PALETTE = {
  critical: { border: '#ef4444', bg: '#fef2f2', badge: '#ef444418', badgeTxt: '#b91c1c', valueTxt: '#b91c1c' },
  warning:  { border: '#f59e0b', bg: '#fffbeb', badge: '#f59e0b18', badgeTxt: '#92400e', valueTxt: '#b45309' },
  good:     { border: '#22c55e', bg: '#f0fdf4', badge: '#22c55e18', badgeTxt: '#15803d', valueTxt: '#15803d' },
  info:     { border: C.blue,   bg: '#eff6ff', badge: `${C.blue}18`, badgeTxt: C.navy,   valueTxt: C.navy   },
  neutral:  { border: '#94a3b8', bg: '#f8fafc', badge: '#94a3b818', badgeTxt: '#475569', valueTxt: '#1e293b' },
};

function AnalyticCard({ label, value, sub, detail, risk = 'neutral', badge, delta, miniBar, sparkData }) {
  const p = RISK_PALETTE[risk] || RISK_PALETTE.neutral;
  const deltaPositive = delta > 0;
  return (
    <div style={{
      background: p.bg,
      border: `1.5px solid ${p.border}44`,
      borderLeft: `4px solid ${p.border}`,
      borderRadius: 12,
      padding: '14px 16px',
      position: 'relative',
      overflow: 'hidden',
      minWidth: 0,
    }}>
      {badge && (
        <span style={{
          position: 'absolute', top: 10, right: 10,
          background: p.badge, color: p.badgeTxt,
          fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
          textTransform: 'uppercase', padding: '2px 7px', borderRadius: 20,
        }}>{badge}</span>
      )}
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, color: p.valueTxt, lineHeight: 1, marginBottom: 4 }}>
        {value}
        {delta !== undefined && delta !== null && (
          <span style={{ fontSize: 12, fontWeight: 700, color: deltaPositive ? C.red : C.green, marginLeft: 8 }}>
            {deltaPositive ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
      {sub && <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.4, marginBottom: detail ? 4 : 0 }}>{sub}</div>}
      {detail && <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.4, fontStyle: 'italic' }}>{detail}</div>}
      {miniBar !== undefined && (
        <div style={{ marginTop: 8 }}>
          <div style={{ height: 5, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(miniBar, 100)}%`, height: '100%', background: p.border, borderRadius: 3, transition: 'width 0.6s ease' }} />
          </div>
          <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{miniBar.toFixed(1)}% of target</div>
        </div>
      )}
      {sparkData && sparkData.length > 1 && (
        <svg viewBox={`0 0 ${sparkData.length * 10} 30`} style={{ width: '100%', height: 28, marginTop: 6 }} preserveAspectRatio="none">
          {(() => {
            const max = Math.max(...sparkData, 1);
            const pts = sparkData.map((v, i) => `${i * 10 + 1},${28 - Math.round((v / max) * 24)}`).join(' ');
            return <polyline points={pts} fill="none" stroke={p.border} strokeWidth="2" strokeLinejoin="round" />;
          })()}
        </svg>
      )}
    </div>
  );
}

// kept for backwards-compat (FY comparison uses it elsewhere via legacy path)
function KpiCard({ icon, label, value, sub, color, bg, suffix }) {
  return (
    <div style={{
      background: bg || '#fff',
      border: `1.5px solid ${color}33`,
      borderRadius: 14,
      padding: '16px 20px',
      flex: 1,
      minWidth: 160,
    }}>
      <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>
        {value}{suffix && <span style={{ fontSize: 16, marginLeft: 2 }}>{suffix}</span>}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.text3, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function AttritionOccupancyDashboard() {
  const [dashboard, setDashboard]       = useState(null);
  const [filterOpts, setFilterOpts]     = useState({});
  const [filters, setFilters]           = useState({});
  const [loading, setLoading]           = useState(false);
  const [syncLoading, setSyncLoading]   = useState(false);
  const [uploadLoading, setUploadLoading]   = useState(false);
  const [sampleLoading, setSampleLoading]   = useState(false);
  const [error, setError]                   = useState('');
  const fileInputRef                        = useRef(null);
  const [activeTab, setActiveTab]       = useState('dashboard'); // dashboard | records | occupancy | strength

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    // Load dashboard and filter options independently so one failure doesn't block the other
    const [dashResult, optsResult] = await Promise.allSettled([
      attritionOccupancyService.getDashboard(filters),
      attritionOccupancyService.getFilterOptions(),
    ]);
    if (dashResult.status === 'fulfilled') {
      setDashboard(dashResult.value);
    } else {
      const msg = dashResult.reason?.message || '';
      // Timeout / network errors before first sync → treat as empty, not broken
      const isTimeoutOrEmpty = msg.includes('timed out') || msg.includes('timeout') || msg.includes('Network');
      if (!isTimeoutOrEmpty) {
        setError(msg || 'Failed to load dashboard');
      }
    }
    if (optsResult.status === 'fulfilled') {
      setFilterOpts(optsResult.value);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncLoading(true);
    try {
      await api.post('/sharepoint-sync/sync-attrition-occupancy', {}, { timeout: 15000 });
      antMsg.info('Sync started — data will refresh automatically in 40 seconds', 6);
      // Reload dashboard after the background job has had time to finish
      setTimeout(() => {
        load();
        setSyncLoading(false);
      }, 40000);
    } catch (e) {
      antMsg.error('SharePoint sync failed: ' + (e?.response?.data?.message || e?.message || 'Unknown error'), 8);
      setSyncLoading(false);
    }
  };

  const handleDownloadSample = async () => {
    setSampleLoading(true);
    try {
      const res = await api.get('/attrition-occupancy/sample-format', { responseType: 'blob', timeout: 30000 });
      const url  = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href     = url;
      link.download = 'Attrition_Upload_Format.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      antMsg.error('Could not download sample: ' + (e?.message || 'Unknown error'));
    } finally {
      setSampleLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ''; // reset so same file can be re-uploaded
    setUploadLoading(true);
    antMsg.loading({ content: `Uploading "${file.name}" — please wait, large files may take up to 2 minutes…`, key: 'upload', duration: 0 });
    try {
      const res = await attritionOccupancyService.uploadExcel(file);
      antMsg.success({ content: `✓ Imported ${res.inserted} records from "${res.sheet}" (${res.totalRows} rows in file)`, key: 'upload', duration: 6 });
      load();
    } catch (err) {
      antMsg.error({ content: 'Upload failed: ' + (err?.response?.data?.message || err?.message || 'Unknown error'), key: 'upload', duration: 8 });
    } finally {
      setUploadLoading(false);
    }
  };

  const kpi = dashboard?.kpi || {};

  const tabs = [
    { key: 'dashboard',  label: '📊 Occupancy Dashboard' },
    { key: 'cluster',    label: '🗂️ Cluster Attrition Dashboard' },
    { key: 'records',    label: '📋 Attrition Records' },
    { key: 'occupancy',  label: '🏢 Occupancy Breakdown' },
    { key: 'taTracker',  label: '🎯 TA Tracker' },
    { key: 'strength',   label: '⚙️ Section Strength' },
    { key: 'clusterCfg', label: '⚙️ Cluster Config' },
  ];

  return (
    <div style={{ background: C.surf, minHeight: '100vh' }}>

      {/* ── Blue header banner ──────────────────────────────────── */}
      <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, #1E6FBF 100%)`, padding: '28px 32px 24px' }}>
        <div style={{ color: '#fff', fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
          Attrition &amp; Occupancy Dashboard
        </div>
        <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 13 }}>
          Live data from SharePoint · section strength vs actual headcount
        </div>

        {/* Tab bar + Sync button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '7px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13,
                  background: activeTab === t.key ? '#fff' : 'rgba(255,255,255,0.12)',
                  color:      activeTab === t.key ? C.navy  : 'rgba(255,255,255,0.85)',
                  transition: 'all 0.15s',
                }}>
                {t.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
            <Tooltip title="Download a sample Excel file showing the required column format">
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadSample}
                loading={sampleLoading}
                style={{
                  borderRadius: 8,
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'rgba(255,255,255,0.85)',
                  fontWeight: 600,
                }}>
                Sample Format
              </Button>
            </Tooltip>
            <Button
              icon={<UploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
              loading={uploadLoading}
              style={{
                borderRadius: 8,
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 700,
              }}>
              {uploadLoading ? 'Importing…' : 'Upload Excel'}
            </Button>
            <Button
              icon={<SyncOutlined spin={syncLoading} />}
              onClick={handleSync}
              loading={syncLoading}
              style={{
                borderRadius: 8,
                background: 'rgba(255,255,255,0.18)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                fontWeight: 700,
              }}>
              {syncLoading ? 'Syncing…' : 'Sync from SharePoint'}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Filter bar ──────────────────────────────────────────── */}
      <div style={{ background: '#fff', padding: '12px 32px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterOutlined style={{ color: C.text3 }} />
        {[
          { key: 'division',    label: 'Division',    opts: filterOpts.divisions },
          { key: 'zone',        label: 'Zone',        opts: filterOpts.zones },
          { key: 'hq',          label: 'HQ',          opts: filterOpts.hqs },
          { key: 'designation', label: 'Designation', opts: filterOpts.designations },
          { key: 'resignRetain',label: 'Resign/Retain',opts: filterOpts.resignRetain },
        ].map(f => (
          <Select key={f.key} allowClear placeholder={f.label} style={{ minWidth: 150 }}
            value={filters[f.key]}
            onChange={v => setFilters(prev => ({ ...prev, [f.key]: v }))}>
            {(f.opts || []).map(o => <Option key={o} value={o}>{o}</Option>)}
          </Select>
        ))}
        <Select allowClear placeholder="Year" style={{ width: 100 }}
          value={filters.yearFrom}
          onChange={v => setFilters(prev => ({ ...prev, yearFrom: v, yearTo: v }))}>
          {(filterOpts.years || []).map(y => <Option key={y} value={y}>{y}</Option>)}
        </Select>
        <Button icon={<ReloadOutlined />} onClick={() => { setFilters({}); }} style={{ borderRadius: 8 }}>
          Clear
        </Button>
        <Button type="primary" onClick={load} loading={loading} style={{ borderRadius: 8, background: C.navy }}>
          Apply
        </Button>
      </div>

      <div style={{ padding: '24px 32px' }}>
        {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16, borderRadius: 10 }} onClose={() => setError('')} />}

        <Spin spinning={loading}>
          {activeTab === 'dashboard'  && <DashboardTab kpi={kpi} dashboard={dashboard} onSync={handleSync} syncing={syncLoading} />}
          {activeTab === 'cluster'    && <ClusterDashboardTab />}
          {activeTab === 'records'    && <RecordsTab filters={filters} />}
          {activeTab === 'occupancy'  && <OccupancyTab />}
          {activeTab === 'taTracker'  && <TaTrackerTab />}
          {activeTab === 'strength'   && <SectionStrengthTab />}
          {activeTab === 'clusterCfg' && <ClusterConfigTab />}
        </Spin>
      </div>
    </div>
  );
}

// ══ Dashboard Tab ══════════════════════════════════════════════════
function DashboardTab({ kpi, dashboard, onSync, syncing }) {
  const [matrix, setMatrix]               = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [aging, setAging]                 = useState(null);
  const [agingLoading, setAgingLoading]   = useState(false);
  const [clusterData, setClusterData]     = useState(null);

  useEffect(() => {
    setMatrixLoading(true);
    attritionOccupancyService.getDivisionDesignationMatrix()
      .then(setMatrix)
      .catch(() => setMatrix(null))
      .finally(() => setMatrixLoading(false));

    setAgingLoading(true);
    attritionOccupancyService.getVacantAging()
      .then(setAging)
      .catch(() => setAging(null))
      .finally(() => setAgingLoading(false));

    clusterService.getClusterDashboard({ from: '2026-04', to: '2027-03' })
      .then(setClusterData)
      .catch(() => setClusterData(null));
  }, []);

  if (!dashboard || !dashboard.kpi) return (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#1B2D6B', marginBottom: 8 }}>No data yet</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 24 }}>
        Sync data from SharePoint to populate the dashboard.
      </div>
      <Button type="primary" icon={<SyncOutlined />} loading={syncing} onClick={onSync}
        style={{ background: '#1B2D6B', borderRadius: 8, fontWeight: 600 }}>
        Sync from SharePoint
      </Button>
    </div>
  );

  const ann   = kpi.annualizedAttrition     ?? 0;
  const q1Ann = kpi.q1AnnualizedAttrition   ?? 0;
  const occ   = kpi.occupancyPct            ?? 0;

  const fy = dashboard.fyComparison || {};
  const fy25 = fy.fy2526 || {};
  const fy26 = fy.fy2627 || {};

  // ── Derived analytical signals ──────────────────────────────────
  const totalResigned  = kpi.totalResigned  ?? 0;
  const totalOnboarded = kpi.totalOnboarded ?? 0;
  const vacancies      = kpi.vacancies      ?? 0;
  const sanctioned     = kpi.totalSanctioned ?? 0;
  const filled         = kpi.actualHeadcount ?? 0;
  const netHC          = totalOnboarded - totalResigned;

  // Monthly resign run-rate from byMonth (current FY = Apr 2026 onwards)
  const byMonthArr = Array.isArray(dashboard.byMonth) ? dashboard.byMonth : [];
  const fy26Months = byMonthArr.filter(m => m.year > 2025 || (m.year === 2025 && ['April','May','June','July','August','September','October','November','December'].includes(m.month)));
  // Only count months that have any data as elapsed
  const currentFYElapsed = (fy26.months ?? 1);
  const monthlyRunRate   = currentFYElapsed > 0 ? (fy26.resigned ?? 0) / currentFYElapsed : 0;

  // FY-end projection: remaining months in FY26-27 (12 - elapsed)
  const monthsRemaining      = Math.max(12 - currentFYElapsed, 0);
  const projectedExtraExits  = Math.round(monthlyRunRate * monthsRemaining);
  const projectedTotalExits  = (fy26.resigned ?? 0) + projectedExtraExits;
  const projectedAttritionPct = sanctioned > 0
    ? +((projectedTotalExits / sanctioned) * 100).toFixed(1) : 0;

  // Replacement rate: onboarded ÷ resigned (in current FY)
  const fy26Onboarded  = byMonthArr.filter(m => m.year >= 2026).reduce((s, m) => s + (m.onboarded || 0), 0);
  const replRate       = (fy26.resigned ?? 0) > 0 ? +(fy26Onboarded / (fy26.resigned ?? 1)).toFixed(2) : null;
  const replRisk       = replRate === null ? 'neutral' : replRate < 0.5 ? 'critical' : replRate < 0.8 ? 'warning' : 'good';

  // Top exit reason
  const byReason = dashboard.byReason || {};
  const topReasonEntry = Object.entries(byReason).sort((a, b) => b[1] - a[1])[0];
  const topReason    = topReasonEntry?.[0] || '—';
  const topReasonCnt = topReasonEntry?.[1] || 0;
  const topReasonPct = totalResigned > 0 ? +((topReasonCnt / totalResigned) * 100).toFixed(0) : 0;

  // Attrition hotspot (division with most resigns) — backend already filters to active divisions only
  const divRows0 = Object.entries(dashboard.byDivision || {})
    .map(([div, val]) => ({ div, ...(typeof val === 'object' ? val : {}) }))
    .filter(r => (r.resigned || 0) > 0)
    .sort((a, b) => (b.resigned || 0) - (a.resigned || 0));
  const hotspot    = divRows0[0];
  const hotspotPct = hotspot && sanctioned > 0
    ? +((hotspot.resigned / sanctioned) * 100).toFixed(1) : 0;

  // Q1 FY trend arrow: Q1 this year vs last year Q1 (Apr-May-Jun 2025 vs Apr-May-Jun 2026)
  const q1_25 = byMonthArr.filter(m => m.year === 2025 && ['April','May','June'].includes(m.month)).reduce((s, m) => s + (m.resign || 0), 0);
  const q1_26 = byMonthArr.filter(m => m.year === 2026 && ['April','May','June'].includes(m.month)).reduce((s, m) => s + (m.resign || 0), 0);
  const q1YoYDelta = q1_26 - q1_25;

  // Spark data for attrition trend (last 6 months resign counts)
  const last6 = byMonthArr.slice(-6).map(m => m.resign || 0);

  // attrition trend risk vs last FY
  const attrDelta = +(fy26.annualizedAttrition ?? 0) - +(fy25.annualizedAttrition ?? 0);
  const attrRisk  = (fy26.annualizedAttrition ?? 0) >= 30 ? 'critical' : (fy26.annualizedAttrition ?? 0) >= 20 ? 'warning' : 'good';

  // Division table columns
  const divisionCols = [
    { title: 'Division',         key: 'div',     render: (_, r) => <b>{r.div}</b> },
    { title: 'Approved HC',      key: 'sanctioned', align: 'right',
      render: (_, r) => <span>{r.sanctioned || '—'}</span> },
    { title: 'Resigned (YTD)',   key: 'resigned',  align: 'right',
      render: (_, r) => <span style={{ color: r.resigned > 0 ? C.red : C.text3, fontWeight: 600 }}>{r.resigned}</span> },
    { title: 'On-boarded (YTD)',  key: 'onboarded', align: 'right',
      render: (_, r) => <span style={{ color: r.onboarded > 0 ? C.green : C.text3, fontWeight: 600 }}>{r.onboarded}</span> },
    { title: 'Net',             key: 'net',       align: 'right',
      render: (_, r) => {
        const net = r.onboarded - r.resigned;
        return <span style={{ color: net >= 0 ? C.green : C.red, fontWeight: 700 }}>{net >= 0 ? '+' : ''}{net}</span>;
      }},
    { title: 'Attrition %',    key: 'attr',     align: 'right',
      render: (_, r) => {
        if (!r.sanctioned) return '—';
        const pct = ((r.resigned / r.sanctioned) * 100).toFixed(1);
        const color = parseFloat(pct) >= 15 ? C.red : parseFloat(pct) >= 10 ? C.amber : C.green;
        return <span style={{ color, fontWeight: 700 }}>{pct}%</span>;
      }},
  ];

  // byMonth is an array from the new backend, but may be a plain object from the old one
  const byMonthData = Array.isArray(dashboard.byMonth)
    ? dashboard.byMonth
    : Object.entries(dashboard.byMonth || {}).map(([key, val]) => {
        const parts = key.split(' ');
        const month = parts.slice(0, -1).join(' ') || key;
        const year  = parseInt(parts[parts.length - 1]) || 0;
        return {
          key,
          month,
          year,
          resign:    typeof val === 'number' ? val : (val?.resign    || 0),
          onboarded: typeof val === 'object' ? (val?.onboarded || 0) : 0,
          label: `${(month || '').slice(0, 3)}-${String(year).slice(-2)}`,
        };
      });

  const divisionRows = Object.entries(dashboard.byDivision || {})
    .map(([div, val]) => ({ div, ...(typeof val === 'object' ? val : {}) }))
    .sort((a, b) => (b.resigned || 0) - (a.resigned || 0));

  // ── Deduplicate & sort month data in FY order ─────────────────
  const _mmMap = {};
  byMonthData.forEach(m => {
    const k = `${m.month}|${m.year}`;
    if (_mmMap[k]) {
      _mmMap[k].resign     = (_mmMap[k].resign     || 0) + (m.resign     || 0);
      _mmMap[k].onboarded  = (_mmMap[k].onboarded  || 0) + (m.onboarded  || 0);
    } else {
      _mmMap[k] = { ...m };
    }
  });
  const _calMonOrder = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const byMonthSorted = Object.values(_mmMap)
    .sort((a, b) => {
      const aMs = a.year * 12 + (_calMonOrder.indexOf(a.month));
      const bMs = b.year * 12 + (_calMonOrder.indexOf(b.month));
      return aMs - bMs;
    })
    .map(m => ({ ...m, label: `${(m.month || '').slice(0, 3)}-${String(m.year).slice(-2)}` }));

  // ── Current month data ────────────────────────────────────────
  const now = new Date();
  const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const curMonthName = MONTHS_EN[now.getMonth()];
  const curYear      = now.getFullYear();
  const curMonthRow  = byMonthSorted.find(m => m.month === curMonthName && m.year === curYear) || null;
  const curResigned  = curMonthRow?.resign    ?? 0;
  const curOnboarded = curMonthRow?.onboarded ?? 0;
  const curNet       = curOnboarded - curResigned;
  const curAttrPct   = sanctioned > 0 ? +((curResigned / sanctioned) * 100).toFixed(2) : 0;
  const curLabel     = `${curMonthName.slice(0,3)}-${String(curYear).slice(-2)}`;

  // Enhanced monthly table with attrition % + YTD cumulative
  let ytdSum = 0;
  const byMonthEnriched = byMonthSorted.map(m => {
    ytdSum += (m.resign || 0);
    const attrPct = sanctioned > 0 ? +((m.resign / sanctioned) * 100).toFixed(2) : null;
    const isCurrent = m.month === curMonthName && m.year === curYear;
    return { ...m, attrPct, ytdResigned: ytdSum, isCurrent };
  });

  // Monthly trend columns
  const maxBarVal = Math.max(...byMonthSorted.map(m => Math.max(m.resign || 0, m.onboarded || 0)), 1);
  const monthCols = [
    { title: 'Month', key: 'label', width: 90,
      render: (_, r) => (
        <span style={{ fontWeight: 700, color: r.isCurrent ? C.blue : C.text1,
          display: 'flex', alignItems: 'center', gap: 4 }}>
          {r.isCurrent && <span style={{ fontSize: 9, background: C.blue, color: '#fff',
            borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>NOW</span>}
          {r.label}
        </span>
      )},
    { title: 'Resigned', key: 'resign', align: 'right', width: 80,
      render: (_, r) => <span style={{ color: C.red, fontWeight: 700 }}>{r.resign ?? 0}</span> },
    { title: 'On-boarded', key: 'onboarded', align: 'right', width: 95,
      render: (_, r) => <span style={{ color: C.green, fontWeight: 700 }}>{r.onboarded ?? 0}</span> },
    { title: 'Net', key: 'net', align: 'right', width: 60,
      render: (_, r) => {
        const net = (r.onboarded || 0) - (r.resign || 0);
        return <span style={{ color: net >= 0 ? C.green : C.red, fontWeight: 700 }}>{net >= 0 ? '+' : ''}{net}</span>;
      }},
    { title: 'Attrition %', key: 'attrPct', align: 'right', width: 90,
      render: (_, r) => {
        if (r.attrPct === null) return <span style={{ color: C.text3 }}>—</span>;
        const color = r.attrPct >= 3 ? C.red : r.attrPct >= 1.5 ? C.amber : C.green;
        return <span style={{ color, fontWeight: 700 }}>{r.attrPct}%</span>;
      }},
    { title: 'YTD Resigned', key: 'ytdResigned', align: 'right', width: 100,
      render: (_, r) => <span style={{ color: C.text2, fontWeight: 600 }}>{r.ytdResigned}</span> },
    { title: 'Trend', key: 'bar', width: 120,
      render: (_, r) => {
        const rW = Math.round(((r.resign || 0) / maxBarVal) * 100);
        const oW = Math.round(((r.onboarded || 0) / maxBarVal) * 100);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: rW, height: 7, background: C.red, borderRadius: 4, minWidth: rW > 0 ? 4 : 0 }} />
              {r.resign > 0 && <span style={{ fontSize: 9, color: C.text3 }}>R</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: oW, height: 7, background: C.green, borderRadius: 4, minWidth: oW > 0 ? 4 : 0 }} />
              {r.onboarded > 0 && <span style={{ fontSize: 9, color: C.text3 }}>O</span>}
            </div>
          </div>
        );
      }},
  ];

  return (
    <>
      {/* ── Period label ──────────────────────────────────────── */}
      {fy.periodLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ background: `linear-gradient(90deg, ${C.navy}, ${C.blue})`,
            color: '#fff', borderRadius: 8, padding: '5px 16px',
            fontWeight: 700, fontSize: 13, letterSpacing: '0.3px' }}>
            📅 {fy.periodLabel}
          </div>
          <span style={{ fontSize: 12, color: C.text3 }}>
            All data filtered from April 2025 onwards · FY 2025-26 + FY 2026-27 YTD
          </span>
        </div>
      )}

      {/* ── KPI Cards ─────────────────────────────────────────── */}
      {(() => {
        const occAccent = occ >= 90 ? '#22c55e' : occ >= 75 ? '#f59e0b' : '#ef4444';
        const kpiCards = [
          {
            key: 'occ',
            label: 'Occupancy Rate',
            value: sanctioned > 0 ? `${occ.toFixed(1)}%` : '—',
            sub: 'positions filled',
            accent: occAccent,
            border: occAccent,
            bar: true,
          },
          {
            key: 'sanc',
            label: 'Approved HC',
            value: sanctioned.toLocaleString(),
            sub: 'total sanctioned',
            accent: '#3b82f6',
            border: '#3b82f6',
          },
          {
            key: 'active',
            label: 'Active',
            value: filled.toLocaleString(),
            sub: 'filled positions',
            accent: '#22c55e',
            border: '#22c55e',
          },
          {
            key: 'vac',
            label: 'Vacancies',
            value: vacancies.toLocaleString(),
            sub: 'open positions',
            accent: vacancies === 0 ? '#22c55e' : '#ef4444',
            border: vacancies === 0 ? '#22c55e' : '#ef4444',
          },
        ];
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            {kpiCards.map(card => (
              <div key={card.key} style={{
                background: '#fff',
                borderRadius: 10,
                padding: '12px 16px',
                borderLeft: `4px solid ${card.border}`,
                boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                  {card.label}
                </div>
                <div style={{ fontSize: 28, fontWeight: 900, color: card.accent, lineHeight: 1 }}>
                  {card.value}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                  {card.sub}
                </div>
                {card.bar && (
                  <div style={{ marginTop: 6, height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(occ, 100)}%`, background: occAccent, borderRadius: 2, transition: 'width 0.5s ease' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })()}

      <Row gutter={[16, 16]}>
        {/* Month-wise Trend — last 6 months (hidden) */}
        {false && <Col xs={24}>
          {byMonthEnriched.length === 0
            ? null
            : (() => {
                const last6m  = byMonthEnriched.slice(-6);
                const maxVal  = Math.max(...last6m.map(m => Math.max(m.resign || 0, m.onboarded || 0)), 1);
                const BAR_H   = 72;
                const totalResignL6  = last6m.reduce((s, m) => s + (m.resign    || 0), 0);
                const totalOnboardL6 = last6m.reduce((s, m) => s + (m.onboarded || 0), 0);
                const netL6   = totalOnboardL6 - totalResignL6;
                return (
                  <div style={{ background: '#fff', borderRadius: 14, border: `1.5px solid ${C.border}`, overflow: 'hidden', marginBottom: 4 }}>
                    {/* Header */}
                    <div style={{
                      background: 'linear-gradient(135deg, #1B2D6B 0%, #1E6FBF 100%)',
                      padding: '14px 20px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 16 }}>📆</span>
                        <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>Monthly wise Onboard Vs Resign — Last 6 Months</span>
                        <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>to {curLabel}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 20 }}>
                        {[
                          { label: 'Resigned', value: totalResignL6,  color: '#fca5a5' },
                          { label: 'On-boarded', value: totalOnboardL6, color: '#86efac' },
                          { label: 'Net', value: (netL6 >= 0 ? '+' : '') + netL6, color: netL6 >= 0 ? '#86efac' : '#fca5a5' },
                        ].map(s => (
                          <div key={s.label} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 16, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 600, marginTop: 2 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Month cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${last6m.length}, 1fr)`, borderTop: `1px solid ${C.border}` }}>
                      {last6m.map((m, idx) => {
                        const net  = (m.onboarded || 0) - (m.resign || 0);
                        const rH   = Math.max(Math.round(((m.resign    || 0) / maxVal) * BAR_H), m.resign    > 0 ? 6 : 0);
                        const oH   = Math.max(Math.round(((m.onboarded || 0) / maxVal) * BAR_H), m.onboarded > 0 ? 6 : 0);
                        const attrColor = (m.attrPct || 0) >= 3 ? '#dc2626' : (m.attrPct || 0) >= 1.5 ? '#f59e0b' : '#64748b';
                        const isLast = idx === last6m.length - 1;
                        return (
                          <div key={m.label} style={{
                            padding: '18px 12px 14px',
                            borderRight: !isLast ? `1px solid ${C.border}` : 'none',
                            background: m.isCurrent ? '#eff6ff' : '#fff',
                            borderBottom: m.isCurrent ? `3px solid ${C.blue}` : '3px solid transparent',
                            transition: 'background 0.15s',
                          }}>
                            {/* Month label */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, marginBottom: 14 }}>
                              {m.isCurrent && (
                                <span style={{
                                  background: C.blue, color: '#fff', fontSize: 8, fontWeight: 800,
                                  borderRadius: 4, padding: '2px 5px', letterSpacing: '0.5px',
                                }}>NOW</span>
                              )}
                              <span style={{
                                fontSize: 12, fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase',
                                color: m.isCurrent ? C.blue : '#475569',
                              }}>{m.label}</span>
                            </div>

                            {/* Bar chart — numbers row, then bars row, then labels */}
                            {/* Numbers above bars (separate row to avoid overflow collision) */}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 4 }}>
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#dc2626', width: 28, textAlign: 'center' }}>{m.resign || 0}</span>
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#16a34a', width: 28, textAlign: 'center' }}>{m.onboarded || 0}</span>
                            </div>
                            {/* Bars fixed-height container, aligned to bottom */}
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 18, height: BAR_H, marginBottom: 0 }}>
                              <div style={{
                                width: 28, height: rH || 3,
                                background: 'linear-gradient(180deg, #f87171 0%, #dc2626 100%)',
                                borderRadius: '4px 4px 0 0',
                                boxShadow: rH > 4 ? '0 2px 6px rgba(220,38,38,0.3)' : 'none',
                              }} />
                              <div style={{
                                width: 28, height: oH || 3,
                                background: 'linear-gradient(180deg, #4ade80 0%, #16a34a 100%)',
                                borderRadius: '4px 4px 0 0',
                                boxShadow: oH > 4 ? '0 2px 6px rgba(22,163,74,0.3)' : 'none',
                              }} />
                            </div>
                            {/* Baseline */}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 4 }}>
                              <div style={{ width: 28, height: 3, background: '#e2e8f0', borderRadius: 2 }} />
                              <div style={{ width: 28, height: 3, background: '#e2e8f0', borderRadius: 2 }} />
                            </div>
                            {/* Labels */}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 10 }}>
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', width: 28, textAlign: 'center' }}>Exit</span>
                              <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px', width: 28, textAlign: 'center' }}>Join</span>
                            </div>

                            {/* Net badge */}
                            <div style={{ textAlign: 'center', marginBottom: 6 }}>
                              <span style={{
                                display: 'inline-block',
                                background: net >= 0 ? '#f0fdf4' : '#fef2f2',
                                color: net >= 0 ? '#15803d' : '#dc2626',
                                fontWeight: 900, fontSize: 15, lineHeight: 1,
                                padding: '4px 12px', borderRadius: 20,
                                border: `1.5px solid ${net >= 0 ? '#bbf7d0' : '#fecaca'}`,
                              }}>
                                {net >= 0 ? '+' : ''}{net}
                              </span>
                            </div>

                            {/* Attrition % */}
                            {/* {m.attrPct !== null && (
                              <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: attrColor }}>
                                {m.attrPct}% attrition
                              </div>
                            )} */}
                          </div>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div style={{ padding: '8px 20px', background: '#f8fafc', borderTop: `1px solid ${C.border}`, display: 'flex', gap: 20, alignItems: 'center' }}>
                      {[
                        { color: '#dc2626', bg: '#fee2e2', label: 'Exit (Resigned)' },
                        { color: '#16a34a', bg: '#dcfce7', label: 'Join (On-boarded)' },
                        { color: netL6 >= 0 ? '#15803d' : '#dc2626', bg: netL6 >= 0 ? '#f0fdf4' : '#fef2f2', label: 'Net = Join − Exit' },
                      ].map(l => (
                        <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                          <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{l.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()
          }
        </Col>}

        {/* Designation breakdown */}
        <Col xs={24}>
          <DesignationBreakdown byDesignation={dashboard.byDesignation} sanctioned={sanctioned} />
        </Col>

        {/* Tabbed panel: Division-wise Summary  |  Cluster × Designation */}
        <Col xs={24}>
          <DashboardBottomTabs
            divisionRows={divisionRows}
            divisionCols={divisionCols}
            matrix={matrix}
            matrixLoading={matrixLoading}
            aging={aging}
            agingLoading={agingLoading}
          />
        </Col>

        {/* Cluster Onboard Pivot Table */}
        {clusterData && (
          <Col xs={24}>
            <ClusterOnboardTable data={clusterData} />
          </Col>
        )}
      </Row>
    </>
  );
}

// ── Tabbed bottom panel ─────────────────────────────────────────────
function DashboardBottomTabs({ divisionRows, divisionCols, matrix, matrixLoading, aging, agingLoading }) {
  const [activeBottomTab, setActiveBottomTab] = useState('matrix');

  const tabs = [
    { key: 'matrix',       label: 'Cluster × Designation' },
    { key: 'division',     label: 'Division-wise Summary' },
    { key: 'aging-div',    label: 'Division-wise Vacant Aging' },
    { key: 'aging-desig',  label: 'Designation-wise Vacant Aging' },
  ];

  const BANDS = ['0-30', '31-60', '61-90', '91-120', '121+'];
  const bandColor = b => b === '0-30' ? '#16a34a' : b === '31-60' ? '#ca8a04' : b === '61-90' ? '#ea580c' : b === '91-120' ? '#dc2626' : '#7f1d1d';

  const agingTh = (txt, extra = {}) => (
    <th style={{
      padding: '7px 10px', textAlign: 'center', fontWeight: 700, fontSize: 11,
      color: '#fff', background: '#1B2D6B', borderRight: '1px solid #334a8a',
      whiteSpace: 'nowrap', ...extra,
    }}>{txt}</th>
  );

  const agingCell = (v, color = '#1e293b', bold = false, bg = '#fff') => (
    <td style={{
      textAlign: 'center', padding: '5px 8px', fontSize: 12,
      fontWeight: bold ? 800 : 500, color: v > 0 ? color : '#cbd5e1',
      borderRight: '1px solid #f1f5f9', background: bg,
    }}>{v > 0 ? v : '—'}</td>
  );

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
      {/* Tab header */}
      <div style={{ background: 'linear-gradient(135deg, #1B2D6B 0%, #1E6FBF 100%)', padding: '0 18px', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveBottomTab(t.key)} style={{
            padding: '12px 16px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
            background: activeBottomTab === t.key ? 'rgba(255,255,255,0.18)' : 'transparent',
            color: activeBottomTab === t.key ? '#fff' : 'rgba(255,255,255,0.65)',
            borderBottom: activeBottomTab === t.key ? '3px solid #fff' : '3px solid transparent',
            transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Cluster × Designation */}
      {activeBottomTab === 'matrix' && (
        <Spin spinning={matrixLoading}>
          <div style={{ overflowX: 'auto' }}>
            <ClusterMatrix data={matrix} />
          </div>
        </Spin>
      )}

      {/* Division-wise Summary */}
      {activeBottomTab === 'division' && (
        <div style={{ padding: 16 }}>
          <Table
            dataSource={divisionRows}
            columns={divisionCols}
            rowKey="div"
            size="small"
            pagination={false}
            style={{ fontSize: 12 }}
            summary={rows => {
              const tot = rows.reduce((s, r) => ({
                sanctioned: s.sanctioned + (r.sanctioned || 0),
                resigned:   s.resigned   + (r.resigned   || 0),
                onboarded:  s.onboarded  + (r.onboarded  || 0),
              }), { sanctioned: 0, resigned: 0, onboarded: 0 });
              const net = tot.onboarded - tot.resigned;
              const attrPct = tot.sanctioned > 0
                ? ((tot.resigned / tot.sanctioned) * 100).toFixed(1) : '—';
              return (
                <Table.Summary.Row style={{ fontWeight: 700, background: '#f1f5f9' }}>
                  <Table.Summary.Cell>Grand Total</Table.Summary.Cell>
                  <Table.Summary.Cell align="right">{tot.sanctioned || '—'}</Table.Summary.Cell>
                  <Table.Summary.Cell align="right"><span style={{ color: C.red }}>{tot.resigned}</span></Table.Summary.Cell>
                  <Table.Summary.Cell align="right"><span style={{ color: C.green }}>{tot.onboarded}</span></Table.Summary.Cell>
                  <Table.Summary.Cell align="right"><span style={{ color: net >= 0 ? C.green : C.red }}>{net >= 0 ? '+' : ''}{net}</span></Table.Summary.Cell>
                  <Table.Summary.Cell align="right"><span style={{ color: parseFloat(attrPct) >= 15 ? C.red : C.amber }}>{attrPct}{attrPct !== '—' ? '%' : ''}</span></Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      )}

      {/* Division-wise Vacant Aging */}
      {activeBottomTab === 'aging-div' && (
        <Spin spinning={agingLoading}>
          <div style={{ overflowX: 'auto' }}>
            {!aging ? <Empty description="No aging data" style={{ padding: 32 }} /> : (() => {
              const clusterOrder = [...new Set(aging.byDivision.map(r => r.cluster))];
              const ctMap = Object.fromEntries((aging.clusterTotals || []).map(c => [c.cluster, c]));
              return (
                <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                  <thead>
                    <tr>
                      {agingTh('Division / Cluster', { textAlign: 'left', minWidth: 180, paddingLeft: 14 })}
                      {BANDS.map(b => agingTh(b + ' days', { background: bandColor(b), minWidth: 80 }))}
                      {agingTh('Total Vacant', { background: '#374151', minWidth: 90 })}
                    </tr>
                  </thead>
                  <tbody>
                    {clusterOrder.map(cluster => {
                      const divs = aging.byDivision.filter(r => r.cluster === cluster);
                      const ct = ctMap[cluster] || {};
                      return (
                        <React.Fragment key={cluster}>
                          {divs.map(row => (
                            <tr key={row.division} style={{ background: '#fffde7', borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '5px 10px 5px 24px', fontWeight: 500, fontSize: 12, color: '#1e293b', borderRight: '1px solid #e5e7eb', minWidth: 180, whiteSpace: 'nowrap' }}>
                                {row.division}
                              </td>
                              {BANDS.map(b => agingCell(row[b] || 0, bandColor(b), false, '#fffde7'))}
                              {agingCell(row.total || 0, '#1e3a8a', false, '#fffde7')}
                            </tr>
                          ))}
                          <tr style={{ background: '#dbeafe', borderBottom: '2px solid #bfdbfe' }}>
                            <td style={{ padding: '6px 10px 6px 12px', fontWeight: 800, fontSize: 13, color: '#1e3a8a', borderRight: '1px solid #bfdbfe', whiteSpace: 'nowrap' }}>
                              {cluster}
                            </td>
                            {BANDS.map(b => agingCell(ct[b] || 0, bandColor(b), true, '#dbeafe'))}
                            {agingCell(ct.total || 0, '#1e3a8a', true, '#dbeafe')}
                          </tr>
                        </React.Fragment>
                      );
                    })}
                    {/* Grand total */}
                    <tr style={{ background: '#1B2D6B', borderTop: '2px solid #0f1f4b' }}>
                      <td style={{ padding: '8px 14px', fontWeight: 900, fontSize: 13, color: '#fff', borderRight: '1px solid #334a8a', whiteSpace: 'nowrap' }}>Grand Total</td>
                      {BANDS.map(b => (
                        <td key={b} style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 800, fontSize: 13,
                          color: (aging.grand?.[b] || 0) > 0 ? '#fde047' : 'rgba(255,255,255,0.3)', borderRight: '1px solid #334a8a' }}>
                          {aging.grand?.[b] || '—'}
                        </td>
                      ))}
                      <td style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 900, fontSize: 14, color: '#fde047', borderRight: '1px solid #334a8a' }}>
                        {aging.grand?.total || 0}
                      </td>
                    </tr>
                  </tbody>
                </table>
              );
            })()}
          </div>
        </Spin>
      )}

      {/* Designation-wise Vacant Aging */}
      {activeBottomTab === 'aging-desig' && (
        <Spin spinning={agingLoading}>
          <div style={{ overflowX: 'auto' }}>
            {!aging ? <Empty description="No aging data" style={{ padding: 32 }} /> : (
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                <thead>
                  <tr>
                    {agingTh('Designation', { textAlign: 'left', minWidth: 140, paddingLeft: 14 })}
                    {BANDS.map(b => agingTh(b + ' days', { background: bandColor(b), minWidth: 90 }))}
                    {agingTh('Total Vacant', { background: '#374151', minWidth: 100 })}
                  </tr>
                </thead>
                <tbody>
                  {(aging.byDesignation || []).map((row, idx) => (
                    <tr key={row.desig} style={{ background: idx % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 10px 8px 16px', fontWeight: 700, fontSize: 13, color: '#1e3a8a', borderRight: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                        {row.desig}
                      </td>
                      {BANDS.map(b => agingCell(row[b] || 0, bandColor(b), false, idx % 2 === 0 ? '#f8fafc' : '#fff'))}
                      {agingCell(row.total || 0, '#1e3a8a', true, idx % 2 === 0 ? '#f8fafc' : '#fff')}
                    </tr>
                  ))}
                  {/* Grand total */}
                  <tr style={{ background: '#1B2D6B', borderTop: '2px solid #0f1f4b' }}>
                    <td style={{ padding: '8px 14px', fontWeight: 900, fontSize: 13, color: '#fff', borderRight: '1px solid #334a8a', whiteSpace: 'nowrap' }}>Grand Total</td>
                    {BANDS.map(b => (
                      <td key={b} style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 800, fontSize: 13,
                        color: (aging.grand?.[b] || 0) > 0 ? '#fde047' : 'rgba(255,255,255,0.3)', borderRight: '1px solid #334a8a' }}>
                        {aging.grand?.[b] || '—'}
                      </td>
                    ))}
                    <td style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 900, fontSize: 14, color: '#fde047', borderRight: '1px solid #334a8a' }}>
                      {aging.grand?.total || 0}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </Spin>
      )}
    </div>
  );
}

// ── Designation alias labels (displayed under each card title) ────
// Maps each designation group key → the actual grade values in ta_tracker (must match DESIG_MAP in backend)
const DESIG_GRADES = {
  BE:  ['BE', 'TBM', 'SR BE', 'BDM', 'TM'],
  ABM: ['ABM', 'Sr ABM'],
  RBM: ['RBM', 'Sr RBM'],
  SM:  ['SM', 'Sr SM'],
  DSM: ['DSM', 'Sr DSM'],
};

const DESIG_META = {
  // MT:  { label: 'MT',  aliases: 'Management Trainee',               color: '#7c3aed', bg: '#f5f3ff' },
  BE:  { label: 'BE',  aliases: 'BE · TBM · SR BE · BDM · TM',     color: '#0e7490', bg: '#ecfeff' },
  ABM: { label: 'ABM', aliases: 'ABM · Sr ABM',                     color: '#1E6FBF', bg: '#eff6ff' },
  DSM: { label: 'DSM', aliases: 'DSM · Sr DSM',                     color: '#d97706', bg: '#fffbeb' },
  RBM: { label: 'RBM', aliases: 'RBM · Sr RBM',                     color: '#1B2D6B', bg: '#f0f4ff' },
  SM:  { label: 'SM',  aliases: 'SM · Sr SM',                       color: '#16a34a', bg: '#f0fdf4' },
};
const DESIG_ORDER = ['BE', 'ABM', 'RBM', 'SM', 'DSM'];

const DD_LIMIT = 100; // records per page in drill-down modal

function DesignationBreakdown({ byDesignation, sanctioned }) {
  const [drillDown, setDrillDown] = useState(null);
  const [ddData, setDdData]       = useState([]);
  const [ddLoading, setDdLoading] = useState(false);
  const [ddTotal, setDdTotal]     = useState(0);
  const [ddPage,  setDdPage]      = useState(1);

  const fetchDd = (grade, status, page = 1) => {
    setDdLoading(true);
    const params = { limit: DD_LIMIT, page, mappedOnly: true };
    if (grade) {
      const gradeList = DESIG_GRADES[grade];
      if (gradeList) params.grades = gradeList.join(',');
      else           params.grade  = grade;
    }
    // 'vacant' in cards = HC - Active = rows where active_vacant != 'active'
    if (status) params.activeVacant = status === 'vacant' ? '!active' : status;
    attritionOccupancyService.getTaRecords(params)
      .then(r => { setDdData(r.data || []); setDdTotal(r.total || 0); })
      .catch(() => { setDdData([]); setDdTotal(0); })
      .finally(() => setDdLoading(false));
  };

  const openDrill = (grade, status, label) => {
    setDrillDown({ grade, status, label });
    setDdPage(1);
    setDdData([]);
    fetchDd(grade, status, 1);
  };

  const gotoPage = (p) => {
    if (!drillDown) return;
    setDdPage(p);
    fetchDd(drillDown.grade, drillDown.status, p);
  };

  if (!byDesignation || Object.keys(byDesignation).length === 0) return null;

  const lnk = { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 };

  const ddCols = [
    { key: 'empCode',      label: 'Emp Code',    w: 90 },
    { key: 'empName',      label: 'Name',        w: 160 },
    { key: 'division',     label: 'Division',    w: 120 },
    { key: 'grade',        label: 'Grade',       w: 70 },
    { key: 'designation',  label: 'Designation', w: 110 },
    { key: 'hq',           label: 'HQ',          w: 100 },
    { key: 'state',        label: 'State',       w: 90 },
    { key: 'activeVacant', label: 'Status',      w: 80 },
  ];

  const rows = DESIG_ORDER
    .map(key => {
      const d = byDesignation[key] || { sanctioned: 0, filled: 0, resigned: 0, onboarded: 0 };
      const vacant   = Math.max(0, d.sanctioned - d.filled);
      const net      = (d.onboarded || 0) - (d.resigned || 0);
      const occPct   = d.sanctioned > 0 ? +((d.filled / d.sanctioned) * 100).toFixed(1) : 0;
      const attrPct  = d.sanctioned > 0 ? +((d.resigned / d.sanctioned) * 100).toFixed(1) : 0;
      return { key, ...DESIG_META[key], ...d, vacant, net, occPct, attrPct };
    })
    .filter(r => r.sanctioned > 0);

  // Others bucket
  const otherData = Object.entries(byDesignation)
    .filter(([k]) => !DESIG_ORDER.includes(k))
    .reduce((acc, [, d]) => {
      acc.sanctioned += d.sanctioned || 0;
      acc.filled     += d.filled     || 0;
      acc.resigned   += d.resigned   || 0;
      acc.onboarded  += d.onboarded  || 0;
      return acc;
    }, { sanctioned: 0, filled: 0, resigned: 0, onboarded: 0 });

  if (otherData.sanctioned > 0) {
    const vacant  = Math.max(0, otherData.sanctioned - otherData.filled);
    const net     = otherData.onboarded - otherData.resigned;
    const occPct  = +((otherData.filled / otherData.sanctioned) * 100).toFixed(1);
    const attrPct = +((otherData.resigned / otherData.sanctioned) * 100).toFixed(1);
    rows.push({ key: 'Other', label: 'Other', aliases: 'Remaining designations',
      color: '#64748b', bg: '#f8fafc', ...otherData, vacant, net, occPct, attrPct });
  }

  const totals = rows.reduce(
    (s, r) => ({ sanctioned: s.sanctioned + r.sanctioned, filled: s.filled + (r.filled||0),
      resigned: s.resigned + r.resigned, onboarded: s.onboarded + r.onboarded }),
    { sanctioned: 0, filled: 0, resigned: 0, onboarded: 0 }
  );
  const totNet      = totals.onboarded - totals.resigned;
  const totAttrPct  = totals.sanctioned > 0 ? +((totals.resigned / totals.sanctioned) * 100).toFixed(1) : 0;
  const totOccPct   = totals.sanctioned > 0 ? +((totals.filled / totals.sanctioned) * 100).toFixed(1) : 0;

  return (
    <>
      {/* Drill-down Modal */}
      {drillDown && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setDrillDown(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90vw', maxWidth: 960,
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <div style={{ background: C.navy, color: '#fff', padding: '12px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>Employee Records</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{drillDown.label}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {!ddLoading && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#86efac' }}>
                    {ddTotal} record{ddTotal !== 1 ? 's' : ''}
                    {ddTotal > DD_LIMIT && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)', fontSize: 11 }}> (pg {ddPage}/{Math.ceil(ddTotal/DD_LIMIT)})</span>}
                  </span>
                )}
                <button onClick={() => setDrillDown(null)} style={{ background: 'rgba(255,255,255,0.15)',
                  border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 6,
                  padding: '4px 10px', fontSize: 16, lineHeight: 1 }}>✕</button>
              </div>
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {ddLoading
                ? <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>
                : ddData.length === 0
                  ? <Empty description="No records found" style={{ padding: 40 }} />
                  : (
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '5px 8px', background: '#f1f5f9', fontWeight: 700, border: '1px solid #e2e8f0',
                            position: 'sticky', top: 0, zIndex: 1, textAlign: 'center', color: '#64748b', fontSize: 10 }}>#</th>
                          {ddCols.map(c => (
                            <th key={c.key} style={{ padding: '5px 8px', background: '#f1f5f9', fontWeight: 700,
                              border: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1,
                              whiteSpace: 'nowrap', color: C.navy, fontSize: 11, minWidth: c.w }}>{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ddData.map((row, idx) => (
                          <tr key={row.id || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td style={{ padding: '4px 8px', border: '1px solid #e2e8f0',
                              textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>{(ddPage - 1) * DD_LIMIT + idx + 1}</td>
                            {ddCols.map(c => (
                              <td key={c.key} style={{ padding: '4px 8px', border: '1px solid #e2e8f0',
                                color: c.key === 'activeVacant' ? (row[c.key] === 'Active' ? '#16a34a' : '#dc2626') : '#1e293b' }}>
                                {row[c.key] || '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
              }
            </div>
            {/* Pagination */}
            {ddTotal > DD_LIMIT && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '10px 16px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <button onClick={() => gotoPage(ddPage - 1)} disabled={ddPage <= 1 || ddLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: '1px solid #cbd5e1',
                    background: ddPage <= 1 ? '#f1f5f9' : C.navy, color: ddPage <= 1 ? '#94a3b8' : '#fff',
                    cursor: ddPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>← Prev</button>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Page {ddPage} of {Math.ceil(ddTotal / DD_LIMIT)} &nbsp;·&nbsp; {ddTotal} total
                </span>
                <button onClick={() => gotoPage(ddPage + 1)} disabled={ddPage >= Math.ceil(ddTotal / DD_LIMIT) || ddLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: '1px solid #cbd5e1',
                    background: ddPage >= Math.ceil(ddTotal / DD_LIMIT) ? '#f1f5f9' : C.navy,
                    color: ddPage >= Math.ceil(ddTotal / DD_LIMIT) ? '#94a3b8' : '#fff',
                    cursor: ddPage >= Math.ceil(ddTotal / DD_LIMIT) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>Next →</button>
              </div>
            )}
          </div>
        </div>
      )}
    <SectionCard title="Designation-wise Breakdown">
      {/* Summary cards row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
        {rows.map(r => {
          const occColor = r.occPct >= 90 ? '#16a34a' : r.occPct >= 75 ? '#d97706' : '#dc2626';
          const aColor   = r.attrPct >= 15 ? '#dc2626' : r.attrPct >= 8 ? '#d97706' : '#16a34a';
          return (
            <div key={r.key} style={{
              background: r.bg,
              borderRadius: 12,
              border: `1.5px solid ${r.color}33`,
              borderTop: `4px solid ${r.color}`,
              padding: '12px 14px',
            }}>
              {/* Designation badge + aliases */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 18, fontWeight: 900, color: r.color, lineHeight: 1 }}>{r.label}</span>
                {r.resigned > 0 && (
                  <span style={{ fontSize: 9, background: '#fee2e2', color: '#b91c1c',
                    borderRadius: 10, padding: '2px 6px', fontWeight: 700 }}>
                    {r.resigned} exits
                  </span>
                )}
              </div>
              <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 8, lineHeight: 1.3 }}>{r.aliases}</div>

              {/* Occupancy bar */}
              <div style={{ height: 5, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden', marginBottom: 4 }}>
                <div style={{ height: '100%', width: `${Math.min(r.occPct, 100)}%`, background: occColor, borderRadius: 3 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: '#64748b', marginBottom: 8 }}>
                <span>Occupancy</span>
                <span style={{ fontWeight: 700, color: occColor }}>{r.occPct}%</span>
              </div>

              {/* Key numbers */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
                {[
                  { l: 'HC',     v: r.sanctioned,  c: '#1e293b',                            st: null },
                  { l: 'Active', v: r.filled || 0, c: '#0e7490',                            st: 'active' },
                  { l: 'Vacant', v: r.vacant,       c: r.vacant > 0 ? '#dc2626' : '#64748b', st: 'vacant' },
                ].map(s => (
                  <div key={s.l} onClick={() => openDrill(r.key, s.st, `${r.label} · ${s.l}`)}
                    style={{ textAlign: 'center', background: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '4px 2px', cursor: 'pointer' }}>
                    <div style={{ ...lnk, fontSize: 14, fontWeight: 800, color: s.c, lineHeight: 1 }}>{s.v}</div>
                    <div style={{ fontSize: 8, color: '#94a3b8', textTransform: 'uppercase', marginTop: 1 }}>{s.l}</div>
                  </div>
                ))}
              </div>

              {/* Attrition + net change
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10 }}>
                <span style={{ color: aColor, fontWeight: 700 }}>Attr: {r.attrPct}%</span>
                <span style={{ color: r.net >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                  Net: {r.net >= 0 ? '+' : ''}{r.net}
                </span>
              </div> */}
            </div>
          );
        })}
      </div>

      {/* Summary table */}
      <Table
        dataSource={rows}
        rowKey="key"
        size="small"
        pagination={false}
        style={{ fontSize: 12 }}
        columns={[
          { title: 'Designation', dataIndex: 'label', key: 'label', width: 90,
            render: (v, r) => <span style={{ fontWeight: 800, color: r.color, fontSize: 13 }}>{v}</span> },
          { title: 'Covers', dataIndex: 'aliases', key: 'aliases',
            render: v => <span style={{ fontSize: 11, color: '#64748b' }}>{v}</span> },
          { title: 'Approved HC', dataIndex: 'sanctioned', key: 'sanctioned', align: 'right', width: 95,
            render: (v, r) => <span style={lnk} onClick={() => openDrill(r.key, null, `${r.label} · All`)}><b>{v}</b></span> },
          { title: 'Active', dataIndex: 'filled', key: 'filled', align: 'right', width: 75,
            render: (v, r) => <span style={{ ...lnk, color: '#0e7490', fontWeight: 600 }} onClick={() => openDrill(r.key, 'active', `${r.label} · Active`)}>{v || 0}</span> },
          { title: 'Vacant', dataIndex: 'vacant', key: 'vacant', align: 'right', width: 75,
            render: (v, r) => <span style={{ ...lnk, color: v > 0 ? '#dc2626' : '#94a3b8', fontWeight: 600 }} onClick={() => openDrill(r.key, 'vacant', `${r.label} · Vacant`)}>{v}</span> },
          { title: 'Occ %', dataIndex: 'occPct', key: 'occPct', align: 'right', width: 70,
            render: v => {
              const c = v >= 90 ? '#16a34a' : v >= 75 ? '#d97706' : '#dc2626';
              return <span style={{ color: c, fontWeight: 700 }}>{v}%</span>;
            }},
          { title: 'Resigned', dataIndex: 'resigned', key: 'resigned', align: 'right', width: 85,
            render: v => <span style={{ color: v > 0 ? '#dc2626' : '#94a3b8', fontWeight: 700 }}>{v}</span> },
          { title: 'Onboarded', dataIndex: 'onboarded', key: 'onboarded', align: 'right', width: 90,
            render: v => <span style={{ color: v > 0 ? '#16a34a' : '#94a3b8', fontWeight: 700 }}>{v}</span> },
          // { title: 'Net', dataIndex: 'net', key: 'net', align: 'right', width: 60,
          //   render: v => <span style={{ color: v >= 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{v >= 0 ? '+' : ''}{v}</span> },
          // { title: 'Attrition %', dataIndex: 'attrPct', key: 'attrPct', align: 'right', width: 90,
          //   render: v => {
          //     const c = v >= 15 ? '#dc2626' : v >= 8 ? '#d97706' : '#16a34a';
          //     return <span style={{ color: c, fontWeight: 700 }}>{v}%</span>;
          //   }},
        ]}
        summary={() => (
          <Table.Summary.Row style={{ fontWeight: 700, background: '#f1f5f9' }}>
            <Table.Summary.Cell><b>Total</b></Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right"><span style={lnk} onClick={() => openDrill(null, null, 'All · Total')}><b>{totals.sanctioned}</b></span></Table.Summary.Cell>
            <Table.Summary.Cell align="right"><span style={{ ...lnk, color: '#0e7490' }} onClick={() => openDrill(null, 'active', 'All · Active')}>{totals.filled}</span></Table.Summary.Cell>
            <Table.Summary.Cell align="right"><span style={{ ...lnk, color: totals.sanctioned - totals.filled > 0 ? '#dc2626' : '#94a3b8' }} onClick={() => openDrill(null, 'vacant', 'All · Vacant')}>{totals.sanctioned - totals.filled}</span></Table.Summary.Cell>
            <Table.Summary.Cell align="right"><span style={{ color: totOccPct >= 90 ? '#16a34a' : totOccPct >= 75 ? '#d97706' : '#dc2626' }}>{totOccPct}%</span></Table.Summary.Cell>
            <Table.Summary.Cell align="right"><span style={{ color: '#dc2626' }}>{totals.resigned}</span></Table.Summary.Cell>
            <Table.Summary.Cell align="right"><span style={{ color: '#16a34a' }}>{totals.onboarded}</span></Table.Summary.Cell>
            {/* <Table.Summary.Cell align="right"><span style={{ color: totNet >= 0 ? '#16a34a' : '#dc2626' }}>{totNet >= 0 ? '+' : ''}{totNet}</span></Table.Summary.Cell>
            <Table.Summary.Cell align="right"><span style={{ color: totAttrPct >= 15 ? '#dc2626' : totAttrPct >= 8 ? '#d97706' : '#16a34a' }}>{totAttrPct}%</span></Table.Summary.Cell> */}
          </Table.Summary.Row>
        )}
      />
    </SectionCard>
    </>
  );
}

function SectionCard({ title, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
      <div style={{ background: C.navy, padding: '10px 16px', color: '#fff', fontWeight: 700, fontSize: 14 }}>
        {title}
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

function BreakdownCard({ title, data = {}, renderRow }) {
  // Guard: arrays are not key-value maps — skip them to avoid rendering object children
  const safeData = Array.isArray(data) ? {} : (data || {});
  const entries = Object.entries(safeData).sort((a, b) => {
    const va = typeof a[1] === 'object' ? (a[1].resigned ?? 0) : Number(a[1]) || 0;
    const vb = typeof b[1] === 'object' ? (b[1].resigned ?? 0) : Number(b[1]) || 0;
    return vb - va;
  });
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
      <div style={{ background: C.navy, padding: '10px 16px', color: '#fff', fontWeight: 700, fontSize: 14 }}>
        {title}
      </div>
      <div style={{ padding: '0 16px', maxHeight: 320, overflowY: 'auto' }}>
        {entries.length === 0
          ? <Empty style={{ padding: 24 }} description="No data" />
          : entries.map(([k, v]) => renderRow(k, v))}
      </div>
    </div>
  );
}

// ══ Records Tab ═════════════════════════════════════════════════════
function RecordsTab({ filters }) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    attritionOccupancyService.getRecords(filters)
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [filters]);

  const columns = [
    { title: 'Emp Code',    dataIndex: 'empCode',       key: 'empCode',   width: 100, fixed: 'left' },
    { title: 'Name',        dataIndex: 'empName',       key: 'empName',   width: 160 },
    { title: 'Division',    dataIndex: 'division',      key: 'division',  width: 120 },
    { title: 'Designation', dataIndex: 'designation',   key: 'designation', width: 130 },
    { title: 'HQ',          dataIndex: 'hq',            key: 'hq',        width: 100 },
    { title: 'Zone',        dataIndex: 'zone',          key: 'zone',      width: 100 },
    { title: 'Join Date',   dataIndex: 'joiningDate',   key: 'joiningDate', width: 110,
      render: v => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Resign Date', dataIndex: 'resignDate',    key: 'resignDate', width: 110,
      render: v => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
    { title: 'Status',      dataIndex: 'resignRetain',  key: 'resignRetain', width: 100,
      render: v => v
        ? <Tag color={v.toLowerCase() === 'resign' ? 'red' : 'green'}>{v}</Tag>
        : '—' },
    { title: 'Reason',      dataIndex: 'reasonOfLeaving', key: 'reasonOfLeaving', width: 180,
      ellipsis: { showTitle: false },
      render: v => v ? <Tooltip title={v}>{v}</Tooltip> : '—' },
    { title: 'Resign Type', dataIndex: 'resignationType', key: 'resignationType', width: 130 },
    { title: 'ABM',         dataIndex: 'abmName',       key: 'abmName',   width: 130 },
    { title: 'RBM',         dataIndex: 'rbmName',       key: 'rbmName',   width: 130 },
    { title: 'SM',          dataIndex: 'smName',        key: 'smName',    width: 130 },
    { title: 'HRBP',        dataIndex: 'hrbp',          key: 'hrbp',      width: 120 },
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
      <div style={{ background: C.navy, padding: '10px 16px', color: '#fff', fontWeight: 700, fontSize: 14 }}>
        Attrition Records <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 8, opacity: 0.7 }}>({data.length} rows)</span>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 1600 }}
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `Total ${t} records` }}
        style={{ fontSize: 12 }}
      />
    </div>
  );
}

// ── Occupancy colour helpers ───────────────────────────────────────
const occColor  = pct => pct >= 90 ? '#16a34a' : pct >= 75 ? '#d97706' : '#dc2626';
const occBg     = pct => pct >= 90 ? '#f0fdf4' : pct >= 75 ? '#fffbeb' : '#fef2f2';
const occBadge  = pct => ({
  background: occBg(pct),
  color:      occColor(pct),
  border:     `1px solid ${occColor(pct)}44`,
  borderRadius: 6,
  padding: '2px 7px',
  fontWeight: 700,
  fontSize: 12,
  display: 'inline-block',
  minWidth: 52,
  textAlign: 'center',
});

// Stacked bar: green (filled) + red (vacant)
function OccBar({ filled, sanctioned, height = 14 }) {
  const fillPct = sanctioned > 0 ? (filled / sanctioned) * 100 : 0;
  const c = occColor(fillPct);
  return (
    <Tooltip title={`Filled: ${filled}  Vacant: ${sanctioned - filled}  Total: ${sanctioned}`}>
      <div style={{ height, borderRadius: 4, background: '#fca5a522', overflow: 'hidden',
        display: 'flex', cursor: 'default', border: '1px solid #e2e8f0' }}>
        <div style={{ width: `${fillPct}%`, background: c, borderRadius: '3px 0 0 3px',
          transition: 'width 0.4s ease', minWidth: fillPct > 0 ? 4 : 0 }} />
        <div style={{ flex: 1, background: '#fca5a544' }} />
      </div>
    </Tooltip>
  );
}

// ══ Cluster × Designation pivot matrix ══════════════════════════════
const DESIG_COLS = ['BE', 'ABM', 'RBM', 'SM', 'DSM'];

function occPctColor(pct) {
  return pct >= 95 ? '#16a34a' : pct >= 90 ? '#d97706' : '#dc2626';
}
function occPctBg(pct) {
  return pct >= 95 ? '#dcfce7' : pct >= 90 ? '#fef3c7' : '#fee2e2';
}

function ClusterMatrix({ data }) {
  const [drillDown, setDrillDown] = useState(null);
  const [ddData, setDdData]       = useState([]);
  const [ddLoading, setDdLoading] = useState(false);
  const [ddTotal, setDdTotal]     = useState(0);
  const [ddPage,  setDdPage]      = useState(1);

  const fetchDd = (division, grade, status, page = 1) => {
    setDdLoading(true);
    const params = { limit: DD_LIMIT, page, mappedOnly: true };
    if (division) params.division = division;
    if (grade) {
      const gradeList = DESIG_GRADES[grade];
      if (gradeList) params.grades = gradeList.join(',');
      else           params.grade  = grade;
    }
    if (status) params.activeVacant = status === 'vacant' ? '!active' : status;
    attritionOccupancyService.getTaRecords(params)
      .then(r => { setDdData(r.data || []); setDdTotal(r.total || 0); })
      .catch(() => { setDdData([]); setDdTotal(0); })
      .finally(() => setDdLoading(false));
  };

  const openDrill = (division, grade, status, label) => {
    setDrillDown({ division, grade, status, label });
    setDdPage(1);
    setDdData([]);
    fetchDd(division, grade, status, 1);
  };

  const gotoPage = (p) => {
    if (!drillDown) return;
    setDdPage(p);
    fetchDd(drillDown.division, drillDown.grade, drillDown.status, p);
  };

  if (!data || !data.clusters || data.clusters.length === 0) {
    return <Empty description="No cluster/division data available" style={{ padding: 32 }} />;
  }

  const gt = data.grandTotal;

  const thBase = {
    padding: '7px 6px', textAlign: 'center', fontWeight: 800, fontSize: 10,
    color: '#fff', background: '#1B2D6B', borderRight: '1px solid #334a8a',
    whiteSpace: 'nowrap',
  };
  const thSub = (label, bg = '#243a80') => (
    <th style={{ ...thBase, background: bg, fontSize: 10, fontWeight: 700, minWidth: 52 }}>{label}</th>
  );

  const calcDesig = (row, d) => {
    const total  = row.desig[d]?.sanctioned || 0;
    const active = row.desig[d]?.filled     || 0;
    const vacant = total - active;
    const pct    = total > 0 ? Math.round((active / total) * 100) : 0;
    return { total, active, vacant, pct };
  };

  const calcGt = d => {
    const total  = gt.desig[d]?.sanctioned || 0;
    const active = gt.desig[d]?.filled     || 0;
    const vacant = total - active;
    const pct    = total > 0 ? Math.round((active / total) * 100) : 0;
    return { total, active, vacant, pct };
  };

  const linkStyle = { cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 2 };

  const subCells = ({ total, active, vacant, pct }, rowBg, bold = false, division = null, desig = null) => {
    const fw = bold ? 800 : 500;
    const vColor = vacant > 0 ? '#dc2626' : vacant < 0 ? '#dc2626' : '#16a34a';
    const vDisp  = vacant < 0 ? `(${Math.abs(vacant)})` : vacant;
    const lbl = `${division || 'All'} · ${desig || 'All'}`;
    return (
      <>
        <td style={{ textAlign: 'center', padding: '5px 5px', fontSize: 11, fontWeight: fw,
          color: '#1e293b', borderRight: '1px solid #e5e7eb', background: rowBg }}>
          <span style={linkStyle} onClick={() => openDrill(division, desig, null, `${lbl} · All`)}>{total}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '5px 5px', fontSize: 11, fontWeight: fw,
          color: '#15803d', borderRight: '1px solid #e5e7eb', background: rowBg }}>
          <span style={linkStyle} onClick={() => openDrill(division, desig, 'active', `${lbl} · Active`)}>{active}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '5px 5px', fontSize: 11, fontWeight: fw,
          color: vColor, borderRight: '1px solid #e5e7eb', background: rowBg }}>
          <span style={linkStyle} onClick={() => openDrill(division, desig, 'vacant', `${lbl} · Vacant`)}>{vDisp}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '4px 5px',
          borderRight: '1px solid #e5e7eb', background: occPctBg(pct) }}>
          <span style={{ fontWeight: bold ? 900 : 700, fontSize: 10, color: occPctColor(pct) }}>{pct}%</span>
        </td>
      </>
    );
  };

  const gtSubCells = ({ total, active, vacant, pct }, desig = null) => {
    const vDisp = vacant < 0 ? `(${Math.abs(vacant)})` : vacant;
    const lbl = `Grand · ${desig || 'All'}`;
    return (
      <>
        <td style={{ textAlign: 'center', padding: '8px 6px', fontWeight: 900, fontSize: 12,
          color: '#fde047', borderRight: '1px solid #334a8a' }}>
          <span style={{ ...linkStyle, color: '#fde047' }} onClick={() => openDrill(null, desig, null, `${lbl} · All`)}>{total}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '8px 6px', fontWeight: 900, fontSize: 12,
          color: '#86efac', borderRight: '1px solid #334a8a' }}>
          <span style={{ ...linkStyle, color: '#86efac' }} onClick={() => openDrill(null, desig, 'active', `${lbl} · Active`)}>{active}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '8px 6px', fontWeight: 900, fontSize: 12,
          color: vacant > 0 ? '#fca5a5' : '#86efac', borderRight: '1px solid #334a8a' }}>
          <span style={{ ...linkStyle, color: vacant > 0 ? '#fca5a5' : '#86efac' }} onClick={() => openDrill(null, desig, 'vacant', `${lbl} · Vacant`)}>{vDisp}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '7px 6px',
          background: occPctBg(pct), borderRight: '1px solid #334a8a' }}>
          <span style={{ fontWeight: 900, fontSize: 12, color: occPctColor(pct) }}>{pct}%</span>
        </td>
      </>
    );
  };

  const renderRow = (row, isDivRow = true) => {
    const bg     = isDivRow ? '#fffde7' : '#dbeafe';
    const txtClr = isDivRow ? '#1e293b' : '#1e3a8a';
    const bold   = !isDivRow;
    return (
      <tr key={row.name} style={{ borderBottom: '1px solid #e5e7eb' }}>
        <td style={{
          padding: '6px 10px 6px ' + (isDivRow ? '24px' : '12px'),
          fontWeight: bold ? 800 : 500, fontSize: isDivRow ? 12 : 13,
          color: txtClr, borderRight: '1px solid #e5e7eb',
          minWidth: 180, whiteSpace: 'nowrap', background: bg,
        }}>{row.name}</td>
        {DESIG_COLS.map(d => (
          <React.Fragment key={d}>{subCells(calcDesig(row, d), bg, bold, isDivRow ? row.name : null, d)}</React.Fragment>
        ))}
        {/* Grand summary for the row */}
        <td style={{ textAlign: 'center', padding: '5px 6px', fontSize: 11, fontWeight: bold ? 800 : 600,
          color: '#1e3a8a', borderRight: '1px solid #cbd5e1', background: '#eff6ff' }}>
          <span style={linkStyle} onClick={() => openDrill(isDivRow ? row.name : null, null, null, `${row.name} · All`)}>{row.totalSanctioned}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '5px 6px', fontSize: 11, fontWeight: bold ? 800 : 600,
          color: '#15803d', borderRight: '1px solid #cbd5e1', background: '#eff6ff' }}>
          <span style={linkStyle} onClick={() => openDrill(isDivRow ? row.name : null, null, 'active', `${row.name} · Active`)}>{row.totalFilled}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '4px 6px',
          borderRight: '1px solid #cbd5e1', background: occPctBg(row.occupancyPct) }}>
          <span style={{ fontWeight: bold ? 900 : 700, fontSize: 11, color: occPctColor(row.occupancyPct) }}>{row.occupancyPct}%</span>
        </td>
        <td style={{ textAlign: 'center', padding: '5px 6px', fontSize: 11, fontWeight: bold ? 800 : 600,
          color: row.vacant > 0 ? '#dc2626' : '#16a34a', borderRight: '1px solid #e5e7eb', background: '#eff6ff' }}>
          <span style={linkStyle} onClick={() => openDrill(isDivRow ? row.name : null, null, 'vacant', `${row.name} · Vacant`)}>{row.vacant < 0 ? `(${Math.abs(row.vacant)})` : row.vacant}</span>
        </td>
      </tr>
    );
  };

  const gtRow = () => {
    const totSanctioned = gt.totalSanctioned;
    const totFilled     = gt.totalFilled;
    const totVacant     = gt.vacant;
    const totPct        = gt.occupancyPct;
    const vDisp         = totVacant < 0 ? `(${Math.abs(totVacant)})` : totVacant;
    return (
      <tr style={{ background: '#1B2D6B', borderTop: '2px solid #0f1f4b' }}>
        <td style={{ padding: '9px 14px', fontWeight: 900, fontSize: 13, color: '#fff',
          borderRight: '1px solid #334a8a', whiteSpace: 'nowrap' }}>Grand Total</td>
        {DESIG_COLS.map(d => (
          <React.Fragment key={d}>{gtSubCells(calcGt(d), d)}</React.Fragment>
        ))}
        <td style={{ textAlign: 'center', padding: '9px 6px', fontWeight: 900, fontSize: 13,
          color: '#fde047', borderRight: '1px solid #334a8a' }}>
          <span style={{ ...linkStyle, color: '#fde047' }} onClick={() => openDrill(null, null, null, 'Grand Total · All')}>{totSanctioned}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '9px 6px', fontWeight: 900, fontSize: 13,
          color: '#86efac', borderRight: '1px solid #334a8a' }}>
          <span style={{ ...linkStyle, color: '#86efac' }} onClick={() => openDrill(null, null, 'active', 'Grand Total · Active')}>{totFilled}</span>
        </td>
        <td style={{ textAlign: 'center', padding: '8px 6px',
          background: occPctBg(totPct), borderRight: '1px solid #334a8a' }}>
          <span style={{ fontWeight: 900, fontSize: 13, color: occPctColor(totPct) }}>{totPct}%</span>
        </td>
        <td style={{ textAlign: 'center', padding: '9px 6px', fontWeight: 900, fontSize: 13,
          color: totVacant > 0 ? '#fca5a5' : '#86efac', borderRight: '1px solid #334a8a' }}>
          <span style={{ ...linkStyle, color: totVacant > 0 ? '#fca5a5' : '#86efac' }} onClick={() => openDrill(null, null, 'vacant', 'Grand Total · Vacant')}>{vDisp}</span>
        </td>
      </tr>
    );
  };

  const ddCols = [
    { key: 'empCode',      label: 'Emp Code',    w: 90 },
    { key: 'empName',      label: 'Name',        w: 160 },
    { key: 'division',     label: 'Division',    w: 120 },
    { key: 'grade',        label: 'Grade',       w: 70 },
    { key: 'designation',  label: 'Designation', w: 110 },
    { key: 'hq',           label: 'HQ',          w: 100 },
    { key: 'state',        label: 'State',       w: 90 },
    { key: 'activeVacant', label: 'Status',      w: 80 },
  ];

  return (
    <>
      {/* Drill-down Modal */}
      {drillDown && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setDrillDown(null)}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '90vw', maxWidth: 960, maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            {/* Modal header */}
            <div style={{ background: C.navy, color: '#fff', padding: '12px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>Employee Records</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>{drillDown.label}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {!ddLoading && (
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#86efac' }}>
                    {ddTotal} record{ddTotal !== 1 ? 's' : ''}
                    {ddTotal > DD_LIMIT && <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.6)', fontSize: 11 }}> (pg {ddPage}/{Math.ceil(ddTotal/DD_LIMIT)})</span>}
                  </span>
                )}
                <button onClick={() => setDrillDown(null)} style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                  cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 16, lineHeight: 1,
                }}>✕</button>
              </div>
            </div>
            {/* Table */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {ddLoading
                ? <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>
                : ddData.length === 0
                  ? <Empty description="No records found" style={{ padding: 40 }} />
                  : (
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '5px 8px', background: '#f1f5f9', fontWeight: 700,
                            border: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1,
                            textAlign: 'center', color: '#64748b', fontSize: 10 }}>#</th>
                          {ddCols.map(c => (
                            <th key={c.key} style={{ padding: '5px 8px', background: '#f1f5f9', fontWeight: 700,
                              border: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1,
                              whiteSpace: 'nowrap', color: C.navy, fontSize: 11, minWidth: c.w }}>{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {ddData.map((row, idx) => (
                          <tr key={row.id || idx} style={{ background: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td style={{ padding: '4px 8px', border: '1px solid #e2e8f0',
                              textAlign: 'center', color: '#94a3b8', fontSize: 11 }}>{(ddPage - 1) * DD_LIMIT + idx + 1}</td>
                            {ddCols.map(c => (
                              <td key={c.key} style={{ padding: '4px 8px', border: '1px solid #e2e8f0',
                                color: c.key === 'activeVacant'
                                  ? (row[c.key] === 'Active' ? '#16a34a' : '#dc2626')
                                  : '#1e293b' }}>
                                {row[c.key] || '—'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
              }
            </div>
            {/* Pagination */}
            {ddTotal > DD_LIMIT && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '10px 16px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <button onClick={() => gotoPage(ddPage - 1)} disabled={ddPage <= 1 || ddLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: '1px solid #cbd5e1',
                    background: ddPage <= 1 ? '#f1f5f9' : C.navy, color: ddPage <= 1 ? '#94a3b8' : '#fff',
                    cursor: ddPage <= 1 ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>← Prev</button>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  Page {ddPage} of {Math.ceil(ddTotal / DD_LIMIT)} &nbsp;·&nbsp; {ddTotal} total
                </span>
                <button onClick={() => gotoPage(ddPage + 1)} disabled={ddPage >= Math.ceil(ddTotal / DD_LIMIT) || ddLoading}
                  style={{ padding: '4px 14px', borderRadius: 6, border: '1px solid #cbd5e1',
                    background: ddPage >= Math.ceil(ddTotal / DD_LIMIT) ? '#f1f5f9' : C.navy,
                    color: ddPage >= Math.ceil(ddTotal / DD_LIMIT) ? '#94a3b8' : '#fff',
                    cursor: ddPage >= Math.ceil(ddTotal / DD_LIMIT) ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 12 }}>Next →</button>
              </div>
            )}
          </div>
        </div>
      )}
    <div style={{ overflowX: 'auto', borderRadius: 0 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          {/* Row 1 — designation group names */}
          <tr>
            <th rowSpan={2} style={{ ...thBase, textAlign: 'left', minWidth: 180, paddingLeft: 14,
              verticalAlign: 'middle', borderBottom: '1px solid #334a8a' }}>
              Division / Cluster
            </th>
            {DESIG_COLS.map(d => (
              <th key={d} colSpan={4} style={{ ...thBase, background: '#1B2D6B',
                borderBottom: '1px solid #334a8a', borderRight: '2px solid #0f1f4b', fontSize: 11 }}>
                {d}
              </th>
            ))}
            <th colSpan={4} style={{ ...thBase, background: '#0e4f9a',
              borderBottom: '1px solid #334a8a', borderRight: '2px solid #0a3870', fontSize: 11 }}>
              Grand Summary
            </th>
          </tr>
          {/* Row 2 — sub-column labels */}
          <tr>
            {DESIG_COLS.map(d => (
              <React.Fragment key={d}>
                {thSub('Total',  '#243a80')}
                {thSub('Active', '#1a5c2a')}
                {thSub('Vacant', '#7f1d1d')}
                {thSub('Occ %', '#374151')}
              </React.Fragment>
            ))}
            {thSub('Total',    '#0e4f9a')}
            {thSub('Active',   '#15803d')}
            {thSub('Occ %',    '#374151')}
            {thSub('Vacancy',  '#991b1b')}
          </tr>
        </thead>
        <tbody>
          {data.clusters.map(cluster => (
            <React.Fragment key={cluster.clusterId}>
              {cluster.divisions.map(div => renderRow(div, true))}
              {renderRow(cluster.clusterTotal, false)}
            </React.Fragment>
          ))}
          {gtRow()}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ══ Occupancy Breakdown Tab ══════════════════════════════════════════
function OccupancyTab() {
  const [stats, setStats]         = useState(null);
  const [matrix, setMatrix]       = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [errMsg, setErrMsg]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [view, setView]           = useState('matrix'); // matrix | division | zone | grade | region

  const loadStats = () => {
    setLoading(true);
    setErrMsg('');
    attritionOccupancyService.getOccupancyStats()
      .then(d => { setStats(d); setErrMsg(''); })
      .catch(err => {
        setStats(null);
        setErrMsg(err?.response?.data?.message || err?.message || 'Failed to load occupancy data');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStats(); }, []);

  useEffect(() => {
    if (view !== 'matrix' || matrix) return;
    setMatrixLoading(true);
    attritionOccupancyService.getDivisionDesignationMatrix()
      .then(setMatrix)
      .catch(() => setMatrix(null))
      .finally(() => setMatrixLoading(false));
  }, [view, matrix]);

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" tip="Loading occupancy data…" /></div>;
  if (!stats)  return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Empty description={errMsg || 'No occupancy data. Ensure employee_import_data is populated.'} />
      {errMsg && <Button type="primary" style={{ marginTop: 16, background: C.navy }} onClick={loadStats}>Retry</Button>}
    </div>
  );

  const o = stats.overall;
  const occ = o.occupancyPct ?? 0;

  // ── KPI tiles ──
  const tiles = [
    { icon: '🎯', label: 'Total Sanctioned', value: o.sanctioned, sub: 'approved positions', color: C.navy,   bg: '#eff6ff' },
    { icon: '✅', label: 'Filled Positions', value: o.filled,     sub: 'active employees',   color: '#16a34a', bg: '#f0fdf4' },
    { icon: '⚠️', label: 'Vacant Positions', value: o.vacant,     sub: 'open slots',         color: '#dc2626', bg: '#fef2f2' },
    { icon: '📈', label: 'Field Occupancy',  value: `${occ}%`,   sub: occ >= 90 ? 'Healthy' : occ >= 75 ? 'Needs attention' : 'Critical',
      color: occColor(occ), bg: occBg(occ) },
  ];

  // ── Active breakdown list ──
  const lists = {
    division: (stats.byDivision || []).slice().sort((a, b) => a.occupancyPct - b.occupancyPct),
    zone:     (stats.byZone     || []).slice().sort((a, b) => a.occupancyPct - b.occupancyPct),
    grade:    (stats.byGrade    || []).slice().sort((a, b) => b.sanctioned - a.sanctioned),
    region:   (stats.byRegion   || []).slice().sort((a, b) => a.occupancyPct - b.occupancyPct),
  };
  const rows = lists[view] || [];

  // ── Top-vacancy table columns ──
  const tableCols = [
    { title: 'Name', dataIndex: 'label', key: 'label', width: 200,
      render: (v, r) => {
        if (view === 'grade') {
          const meta = DESIG_META[v];
          return (
            <div>
              <b style={{ color: meta?.color || C.text1, fontSize: 14 }}>{v || '—'}</b>
              {meta && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{meta.aliases}</div>}
            </div>
          );
        }
        return <b style={{ color: C.text1 }}>{v || '—'}</b>;
      }},
    { title: 'Sanctioned', dataIndex: 'sanctioned', key: 'sanctioned', align: 'right', width: 100,
      render: v => <span style={{ fontWeight: 700, color: C.navy }}>{v}</span> },
    { title: 'Filled', dataIndex: 'filled', key: 'filled', align: 'right', width: 90,
      render: v => <span style={{ fontWeight: 700, color: '#16a34a' }}>{v}</span> },
    { title: 'Vacant', dataIndex: 'vacant', key: 'vacant', align: 'right', width: 90,
      render: v => <span style={{ fontWeight: 700, color: v > 0 ? '#dc2626' : '#16a34a' }}>{v}</span> },
    { title: 'Occupancy %', dataIndex: 'occupancyPct', key: 'occupancyPct', width: 220,
      render: (v, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 80 }}>
            <OccBar filled={r.filled} sanctioned={r.sanctioned} height={12} />
          </div>
          <span style={occBadge(v)}>{v}%</span>
        </div>
      )},
  ];

  // ── Summary rows for the table footer ──
  const totals = rows.reduce((s, r) => ({
    sanctioned: s.sanctioned + r.sanctioned,
    filled:     s.filled     + r.filled,
    vacant:     s.vacant     + r.vacant,
  }), { sanctioned: 0, filled: 0, vacant: 0 });
  const totPct = totals.sanctioned > 0
    ? Math.round(totals.filled / totals.sanctioned * 1000) / 10 : 0;

  const viewBtns = [
    { key: 'matrix',   label: 'Cluster × Designation' },
    { key: 'division', label: 'By Division' },
    { key: 'zone',     label: 'By Zone' },
    { key: 'region',   label: 'By Region' },
    { key: 'grade',    label: 'By Grade / Designation' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── KPI tiles ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        {tiles.map(t => (
          <div key={t.label} style={{
            flex: 1, minWidth: 180,
            background: t.bg, borderRadius: 14,
            border: `1.5px solid ${t.color}33`,
            padding: '18px 22px',
          }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{t.icon}</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: t.color, lineHeight: 1 }}>{t.value}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text3, textTransform: 'uppercase',
              letterSpacing: '0.5px', marginTop: 6 }}>{t.label}</div>
            <div style={{ fontSize: 11, color: t.color, marginTop: 2, fontWeight: 600 }}>{t.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Overall occupancy bar ─────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, padding: '16px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontWeight: 700, color: C.text1, fontSize: 14 }}>Overall Field Force Occupancy</span>
          <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.text3 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, background: '#16a34a', borderRadius: 2, display: 'inline-block' }} />
              Filled ({o.filled})
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, background: '#fca5a5', borderRadius: 2, display: 'inline-block' }} />
              Vacant ({o.vacant})
            </span>
          </div>
        </div>
        <OccBar filled={o.filled} sanctioned={o.sanctioned} height={28} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: C.text3 }}>
          <span>0%</span>
          <span style={{ fontWeight: 700, color: occColor(occ), fontSize: 15 }}>{occ}% occupied</span>
          <span>100%</span>
        </div>
      </div>

      {/* ── Breakdown section ─────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        {/* View switcher */}
        <div style={{ background: C.navy, padding: '12px 20px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
            {view === 'matrix'
              ? 'Cluster × Designation — Sanctioned Strength'
              : view === 'grade'
                ? 'Occupancy Breakdown — By Designation (canonical groups)'
                : 'Occupancy Breakdown — sorted by lowest occupancy first'}
          </span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {viewBtns.map(b => (
              <button key={b.key} onClick={() => setView(b.key)} style={{
                padding: '4px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: 12,
                background: view === b.key ? '#fff' : 'rgba(255,255,255,0.15)',
                color:      view === b.key ? C.navy  : 'rgba(255,255,255,0.85)',
              }}>{b.label}</button>
            ))}
          </div>
        </div>

        {view === 'matrix' ? (
          matrixLoading
            ? <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" tip="Building matrix…" /></div>
            : <ClusterMatrix data={matrix} />
        ) : (
        <Table
          dataSource={rows}
          columns={tableCols}
          rowKey="label"
          size="small"
          pagination={{ pageSize: 30, showSizeChanger: true, showTotal: t => `${t} entries` }}
          style={{ fontSize: 12 }}
          rowClassName={r => r.occupancyPct < 75 ? 'row-critical' : r.occupancyPct < 90 ? 'row-warn' : ''}
          summary={() => (
            <Table.Summary.Row style={{ fontWeight: 700, background: '#f1f5f9' }}>
              <Table.Summary.Cell index={0}><b>Total</b></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right"><span style={{ color: C.navy, fontWeight: 700 }}>{totals.sanctioned}</span></Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right"><span style={{ color: '#16a34a', fontWeight: 700 }}>{totals.filled}</span></Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right"><span style={{ color: '#dc2626', fontWeight: 700 }}>{totals.vacant}</span></Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 80 }}>
                    <OccBar filled={totals.filled} sanctioned={totals.sanctioned} height={12} />
                  </div>
                  <span style={occBadge(totPct)}>{totPct}%</span>
                </div>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
        )}
      </div>

      {/* ── Vacancy hotspots ──────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        <div style={{ background: '#7c3aed', padding: '10px 20px', color: '#fff', fontWeight: 700, fontSize: 14 }}>
          Vacancy Hotspots — Top 10 Divisions with Most Open Positions
        </div>
        <div style={{ padding: '16px 20px' }}>
          {(stats.byDivision || [])
            .filter(r => r.vacant > 0)
            .sort((a, b) => b.vacant - a.vacant)
            .slice(0, 10)
            .map((r, i) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 0', borderBottom: i < 9 ? `1px solid ${C.border}` : 'none' }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#7c3aed22',
                  color: '#7c3aed', fontWeight: 800, fontSize: 12,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ width: 180, fontSize: 13, fontWeight: 600, color: C.text1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {r.label}
                </div>
                <div style={{ flex: 1 }}>
                  <OccBar filled={r.filled} sanctioned={r.sanctioned} height={14} />
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
                    {r.vacant} vacant
                  </span>
                  <span style={occBadge(r.occupancyPct)}>{r.occupancyPct}%</span>
                </div>
              </div>
            ))}
        </div>
      </div>

    </div>
  );
}

// ══ Section Strength Tab ════════════════════════════════════════════
function SectionStrengthTab() {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingRow, setEditingRow]     = useState(null);
  const [form, setForm]           = useState({ division: '', designation: '', hq: '', sanctionedCount: 0, active: true });
  const [error, setError]         = useState('');

  function loadStrengths() {
    setLoading(true);
    attritionOccupancyService.getAllStrengths(showInactive)
      .then(setData).catch(() => setData([]))
      .finally(() => setLoading(false));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStrengths(); }, [showInactive]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingRow) {
        await attritionOccupancyService.updateStrength(editingRow.id, form);
      } else {
        await attritionOccupancyService.createStrength(form);
      }
      setEditingRow(null);
      setForm({ division: '', designation: '', hq: '', sanctionedCount: 0, active: true });
      loadStrengths();
    } catch (e) {
      setError(e?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await attritionOccupancyService.deleteStrength(id);
      loadStrengths();
    } catch (e) {
      setError(e?.response?.data?.message || 'Delete failed');
    }
  };

  const columns = [
    { title: 'Division',    dataIndex: 'division',       key: 'division',    width: 150 },
    { title: 'Designation', dataIndex: 'designation',    key: 'designation', width: 150 },
    { title: 'HQ',          dataIndex: 'hq',             key: 'hq',          width: 120 },
    { title: 'Sanctioned',  dataIndex: 'sanctionedCount',key: 'sanctioned',  width: 110, align: 'center',
      render: v => <span style={{ fontWeight: 700, color: C.blue }}>{v}</span> },
    { title: 'Active',      dataIndex: 'active',         key: 'active',      width: 80, align: 'center',
      render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Yes' : 'No'}</Tag> },
    {
      title: 'Actions', key: 'actions', width: 120, align: 'center',
      render: (_, rec) => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
          <Button size="small" onClick={() => { setEditingRow(rec); setForm({ division: rec.division, designation: rec.designation, hq: rec.hq, sanctionedCount: rec.sanctionedCount, active: rec.active }); }}
            style={{ borderRadius: 6 }}>Edit</Button>
          <Button size="small" danger onClick={() => handleDelete(rec.id)} style={{ borderRadius: 6 }}>Del</Button>
        </div>
      ),
    },
  ];

  const handleSync = async () => {
    setSyncing(true); setSyncResult(null); setError('');
    try {
      const res = await api.post('/attrition-occupancy/sync-section-strength');
      setSyncResult(res.data);
      loadStrengths();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Sync failed');
    }
    setSyncing(false);
  };

  return (
    <div>
      {/* Auto-sync info banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)',
        borderRadius: 10, padding: '10px 18px', marginBottom: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>⏰</span>
          <div>
            <div style={{ color: '#f0f9ff', fontWeight: 700, fontSize: 12 }}>Auto-Sync from Employee Data</div>
            <div style={{ color: '#93c5fd', fontSize: 11 }}>
              Runs daily at <b>2:00 AM IST</b> — pulls Division, Designation, HQ and sanctioned count from employee_import_data.
              {syncResult && (
                <span style={{ color: '#86efac', marginLeft: 8 }}>
                  Last sync: {syncResult.total} rows in {syncResult.durationMs}ms
                  {syncResult.ranAt && ` · ${new Date(syncResult.ranAt).toLocaleString('en-IN')}`}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button loading={syncing} onClick={handleSync}
          style={{ background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
          {syncing ? 'Syncing…' : '↻ Sync Now'}
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 12, borderRadius: 8 }} onClose={() => setError('')} />}

      {/* Add / Edit form */}
      <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, padding: '16px 20px', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: C.navy, marginBottom: 12 }}>
          {editingRow ? '✏️ Edit Section Strength' : '➕ Add Section Strength'}
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {[
            { key: 'division',    label: 'Division',    type: 'text' },
            { key: 'designation', label: 'Designation', type: 'text' },
            { key: 'hq',          label: 'HQ',          type: 'text' },
            { key: 'sanctionedCount', label: 'Sanctioned Count', type: 'number' },
          ].map(f => (
            <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase' }}>{f.label} *</label>
              <input
                type={f.type}
                value={form[f.key]}
                min={f.type === 'number' ? 0 : undefined}
                onChange={e => setForm(p => ({ ...p, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value }))}
                style={{ border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, width: f.type === 'number' ? 120 : 160, outline: 'none' }}
              />
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.text3, textTransform: 'uppercase' }}>Active</label>
            <select value={form.active ? 'true' : 'false'}
              onChange={e => setForm(p => ({ ...p, active: e.target.value === 'true' }))}
              style={{ border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, height: 34 }}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" onClick={handleSave} loading={saving}
              style={{ borderRadius: 8, background: C.navy, height: 34 }}>
              {editingRow ? 'Update' : 'Add'}
            </Button>
            {editingRow && (
              <Button onClick={() => { setEditingRow(null); setForm({ division: '', designation: '', hq: '', sanctionedCount: 0, active: true }); }}
                style={{ borderRadius: 8, height: 34 }}>Cancel</Button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, color: C.text3 }}>{data.length} records</span>
        <label style={{ fontSize: 13, color: C.text3, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        <Table dataSource={data} columns={columns} rowKey="id" loading={loading}
          size="small" pagination={{ pageSize: 50 }} />
      </div>
    </div>
  );
}

// ══ Cluster Onboard Table (shared: used on Occupancy Dashboard + Cluster Dashboard) ══
function ClusterOnboardTable({ data }) {
  const { months, clusters, grandTotal, nFYMonths = 6 } = data;
  const occColor = pct => pct >= 90 ? '#16a34a' : pct >= 75 ? '#d97706' : '#dc2626';

  const TH = ({ children, style = {} }) => (
    <th style={{ background: C.navy, color: '#fff', fontWeight: 700, fontSize: 11,
      padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap',
      border: '1px solid #2d4a8a', position: 'sticky', top: 0, zIndex: 2, ...style }}>
      {children}
    </th>
  );
  const TD = ({ children, style = {}, align = 'right' }) => (
    <td style={{ fontSize: 11, padding: '4px 7px', textAlign: align,
      border: '1px solid #e2e8f0', whiteSpace: 'nowrap', ...style }}>
      {children}
    </td>
  );

  return (
    <div style={{ background: '#fff', border: `1.5px solid ${C.border}`, borderRadius: 12,
      overflow: 'hidden', marginTop: 16 }}>
      <div style={{ background: C.navy, color: '#fff', fontWeight: 700, fontSize: 13,
        padding: '10px 16px', letterSpacing: 0.3 }}>
        Cluster-wise Onboarding Summary
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
          <thead>
            <tr>
              <TH style={{ minWidth: 160, textAlign: 'left' }}>Business Head / Division</TH>
              <TH style={{ minWidth: 80 }}>Approved HC<br/>FY 26-27</TH>
              {months.map(mo => <TH key={mo.key}>{mo.label}</TH>)}
              <TH style={{ background: '#1e40af' }}>YTD<br/>On-board</TH>
              <TH style={{ minWidth: 80 }}>Field<br/>Occupancy %</TH>
              <TH>Current HC</TH>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster, ci) => {
              const rowBg = ci % 2 === 0 ? '#f0f4ff' : '#fff';
              const subHC  = cluster.divisions.reduce((s, d) => s + d.approvedHC, 0);
              const subO   = cluster.divisions.reduce((s, d) => s + d.ytdOnboarded, 0);
              const subFil = cluster.divisions.reduce((s, d) => s + d.currentHeadcount, 0);
              const subOcc = subHC > 0 ? Math.round(subFil / subHC * 1000) / 10 : 0;
              return (
                <React.Fragment key={cluster.clusterId}>
                  {/* Cluster header */}
                  <tr>
                    <td colSpan={3 + months.length + 2}
                      style={{ background: '#e8edf8', padding: '6px 12px', fontWeight: 800,
                        fontSize: 12, color: C.navy, border: '1px solid #cbd5e1',
                        borderLeft: `4px solid ${C.navy}` }}>
                      {cluster.clusterHead}&nbsp;
                      <span style={{ fontWeight: 400, color: C.text3, fontSize: 11 }}>({cluster.clusterTitle})</span>
                    </td>
                  </tr>
                  {/* Division rows */}
                  {cluster.divisions.map(div => (
                    <tr key={div.divisionName} style={{ background: rowBg }}>
                      <TD align="left" style={{ paddingLeft: 20, color: C.text1 }}>{div.divisionName}</TD>
                      <TD style={{ fontWeight: 700, color: C.navy }}>{div.approvedHC || '—'}</TD>
                      {months.map(mo => {
                        const v = div.monthlyData?.[mo.key]?.onboarded || 0;
                        return <TD key={mo.key} style={{ color: v > 0 ? '#16a34a' : C.text3 }}>{v || '—'}</TD>;
                      })}
                      <TD style={{ fontWeight: 700, color: '#16a34a' }}>{div.ytdOnboarded || '—'}</TD>
                      <TD style={{ fontWeight: 700, color: occColor(div.occupancyPct) }}>
                        {div.occupancyPct > 0 ? `${div.occupancyPct}%` : '—'}
                      </TD>
                      <TD style={{ color: C.navy }}>{div.currentHeadcount || '—'}</TD>
                    </tr>
                  ))}
                  {/* Sub-total */}
                  {cluster.divisions.length > 1 && (
                    <tr style={{ background: '#e8edf8', fontStyle: 'italic' }}>
                      <TD align="left" style={{ fontWeight: 700, color: C.navy, paddingLeft: 8 }}>
                        Sub-total ({cluster.clusterTitle})
                      </TD>
                      <TD style={{ fontWeight: 700, color: C.navy }}>{subHC || '—'}</TD>
                      {months.map(mo => {
                        const o = cluster.divisions.reduce((s, d) => s + (d.monthlyData?.[mo.key]?.onboarded || 0), 0);
                        return <TD key={mo.key} style={{ color: o > 0 ? '#16a34a' : C.text3 }}>{o || '—'}</TD>;
                      })}
                      <TD style={{ fontWeight: 800, color: '#16a34a' }}>{subO || '—'}</TD>
                      <TD style={{ fontWeight: 700, color: occColor(subOcc) }}>{subOcc > 0 ? `${subOcc}%` : '—'}</TD>
                      <TD style={{ fontWeight: 700, color: C.navy }}>{subFil || '—'}</TD>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {/* Grand Total */}
            <tr style={{ background: '#f0fdf4', fontWeight: 900 }}>
              <TD align="left" style={{ fontWeight: 900, fontSize: 13, color: C.navy, borderTop: '2px solid #1B2D6B' }}>Grand Total</TD>
              <TD style={{ fontWeight: 900, color: C.navy, borderTop: '2px solid #1B2D6B' }}>{grandTotal.approvedHC}</TD>
              {months.map(mo => {
                const v = grandTotal.monthlyData?.[mo.key]?.onboarded || 0;
                return <TD key={mo.key} style={{ color: v > 0 ? '#16a34a' : C.text3, borderTop: '2px solid #1B2D6B' }}>{v || '—'}</TD>;
              })}
              <TD style={{ fontWeight: 900, color: '#16a34a', fontSize: 13, borderTop: '2px solid #1B2D6B' }}>{grandTotal.ytdOnboarded}</TD>
              <TD style={{ fontWeight: 900, color: occColor(grandTotal.occupancyPct), fontSize: 13, borderTop: '2px solid #1B2D6B' }}>
                {grandTotal.occupancyPct}%
              </TD>
              <TD style={{ fontWeight: 900, color: C.navy, fontSize: 13, borderTop: '2px solid #1B2D6B' }}>{grandTotal.currentHeadcount}</TD>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══ Cluster Dashboard Tab ═══════════════════════════════════════════
// Build month options Apr-2025 → Mar-2027
const MONTH_OPTIONS = (() => {
  const opts = [];
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let y = 2025, m = 3; // Apr 2025 (0-based)
  const endYM = 2027 * 100 + 2; // Mar 2027
  while (y * 100 + m <= endYM) {
    const mm = String(m + 1).padStart(2, '0');
    opts.push({ value: `${y}-${mm}`, label: `${MONTH_NAMES[m]}-${String(y).slice(2)}` });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return opts;
})();

function ClusterDashboardTab() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [errMsg, setErrMsg]       = useState('');
  const [fromMonth, setFromMonth] = useState('2026-04');
  const [toMonth, setToMonth]     = useState('2027-03');
  const [drill, setDrill]         = useState(null);   // { title, emps }
  const [drillLoading, setDrillLoading] = useState(false);

  const load = (from, to) => {
    setLoading(true); setErrMsg('');
    clusterService.getClusterDashboard({ from, to })
      .then(setData)
      .catch(e => setErrMsg(e?.response?.data?.message || e?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(fromMonth, toMonth); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleFromChange = (val) => {
    setFromMonth(val);
    load(val, toMonth);
  };
  const handleToChange = (val) => {
    setToMonth(val);
    load(fromMonth, val);
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" tip="Loading cluster dashboard…" /></div>;
  if (!data || errMsg) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <Empty description={errMsg || 'No cluster data. Please configure clusters and division mappings first.'} />
      <Button type="primary" onClick={() => load(fromMonth, toMonth)} style={{ marginTop: 16, background: C.navy }}>Retry</Button>
    </div>
  );

  const { months, clusters, grandTotal, nFYMonths = 6, unmatchedLossDivisions = [], lossDivDebug = [] } = data;

  const openDrill = (divisionName, mo) => {
    setDrill({ title: `${divisionName} — ${mo.label}`, emps: [] });
    setDrillLoading(true);
    clusterService.clusterLossDrill(divisionName, mo.key)
      .then(emps => setDrill({ title: `${divisionName} — ${mo.label}`, emps }))
      .catch(() => setDrill({ title: `${divisionName} — ${mo.label}`, emps: [] }))
      .finally(() => setDrillLoading(false));
  };

  // Color helpers
  const attrColor = pct => pct >= 30 ? '#dc2626' : pct >= 20 ? '#d97706' : pct >= 10 ? '#16a34a' : '#6b7280';
  const attrBg    = pct => pct >= 30 ? '#fef2f2' : pct >= 20 ? '#fffbeb' : pct >= 10 ? '#f0fdf4' : 'transparent';
  const occColor  = pct => pct >= 90 ? '#16a34a' : pct >= 75 ? '#d97706' : '#dc2626';

  const TH = ({ children, rowSpan, colSpan, style = {} }) => (
    <th rowSpan={rowSpan} colSpan={colSpan} style={{
      background: C.navy, color: '#fff', fontWeight: 700,
      fontSize: 11, padding: '6px 8px', textAlign: 'center',
      whiteSpace: 'nowrap', border: '1px solid #2d4a8a',
      position: 'sticky', top: 0, zIndex: 2, ...style,
    }}>{children}</th>
  );

  const TD = ({ children, style = {}, align = 'right' }) => (
    <td style={{ fontSize: 11, padding: '4px 7px', textAlign: align,
      border: '1px solid #e2e8f0', whiteSpace: 'nowrap', ...style }}>
      {children}
    </td>
  );

  const renderDivRow = (div, isSubRow = false, rowStyle = {}) => (
    <tr key={div.divisionName} style={{ background: isSubRow ? '#fff' : '#f8fafc', ...rowStyle }}>
      <TD align="left" style={{ fontWeight: isSubRow ? 400 : 700, paddingLeft: isSubRow ? 20 : 8, color: C.text1 }}>
        {div.divisionName}
      </TD>
      <TD style={{ fontWeight: 700, color: C.navy }}>{div.approvedHC || '—'}</TD>
      {months.map(mo => {
        const md = div.monthlyData?.[mo.key] || { onboarded: 0 };
        return (
          <TD key={mo.key} style={{ color: md.onboarded > 0 ? '#16a34a' : C.text3 }}>{md.onboarded || '—'}</TD>
        );
      })}
      <TD style={{ fontWeight: 700, color: '#16a34a', background: '#f0fdf422' }}>{div.ytdOnboarded || '—'}</TD>
      <TD style={{ fontWeight: 700, color: occColor(div.occupancyPct) }}>
        {div.occupancyPct > 0 ? `${div.occupancyPct}%` : '—'}
      </TD>
      <TD style={{ fontWeight: 600, color: C.navy }}>{div.currentHeadcount || '—'}</TD>
    </tr>
  );

  return (
    <div>
      {/* Month range selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff',
        border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '10px 16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.text2, whiteSpace: 'nowrap' }}>Period:</span>
        <Select
          value={fromMonth}
          onChange={handleFromChange}
          style={{ width: 120 }}
          size="small"
          options={MONTH_OPTIONS.filter(o => o.value <= toMonth)}
        />
        <span style={{ fontSize: 12, color: C.text3 }}>to</span>
        <Select
          value={toMonth}
          onChange={handleToChange}
          style={{ width: 120 }}
          size="small"
          options={MONTH_OPTIONS.filter(o => o.value >= fromMonth)}
        />
        <Button size="small" onClick={() => load(fromMonth, toMonth)}
          style={{ marginLeft: 4 }}>Refresh</Button>
        <Button size="small" onClick={() => { setFromMonth('2026-04'); setToMonth('2027-03'); load('2026-04', '2027-03'); }}
          style={{ color: C.navy, borderColor: C.navy }}>Reset</Button>
        {data && (
          <span style={{ fontSize: 11, color: C.text3, marginLeft: 'auto' }}>
            {MONTH_OPTIONS.find(o => o.value === fromMonth)?.label} – {MONTH_OPTIONS.find(o => o.value === toMonth)?.label}
            &nbsp;({data.nFYMonths} month{data.nFYMonths !== 1 ? 's' : ''})
          </span>
        )}
      </div>

      {/* Summary KPI strip — Resigned + Attrition from attrition records (same as Loss Summary) */}
      {(() => {
        const lossResigned = grandTotal.lossTotal || 0;
        const lossAttr = grandTotal.approvedHC > 0
          ? Math.round(lossResigned * 12 / Math.max(1, nFYMonths) / grandTotal.approvedHC * 1000) / 10 : 0;
        return (
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Approved HC',    value: grandTotal.approvedHC, color: C.navy },
          { label: 'Current HC',           value: grandTotal.currentHeadcount, color: '#16a34a' },
          { label: 'YTD Resigned',         value: lossResigned, color: '#dc2626' },
          { label: 'YTD On-boarded',       value: grandTotal.ytdOnboarded, color: '#16a34a' },
          { label: 'Annualized Attrition', value: `${lossAttr}%`, color: attrColor(lossAttr) },
          { label: 'Field Occupancy',      value: `${grandTotal.occupancyPct}%`, color: occColor(grandTotal.occupancyPct) },
        ].map(k => (
          <div key={k.label} style={{ flex: 1, minWidth: 140, background: '#fff', borderRadius: 10,
            border: `1.5px solid ${C.border}`, padding: '12px 16px' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 11, color: C.text3, fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{k.label}</div>
          </div>
        ))}
      </div>
        );
      })()}

      {/* Loss data diagnostic panel */}
      {lossDivDebug.length > 0 && (() => {
        const problems = lossDivDebug.filter(d => d.outOfRange > 0 || d.dateParseFail > 0 || !d.isMatched);
        if (!problems.length) return null;
        return (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', background: '#fffbeb', border: '1.5px solid #f59e0b',
              borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, color: '#92400e', listStyle: 'none' }}>
              ⚠ {problems.length} division(s) have count discrepancies — click to inspect
            </summary>
            <div style={{ overflowX: 'auto', marginTop: 4 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#fef3c7' }}>
                    {['Raw Name in Records','Resolved To','Matched?','Total Resign','In Range','Out of Range','Date Parse Fail','Out-of-Range Samples'].map(h => (
                      <th key={h} style={{ padding: '5px 10px', border: '1px solid #d97706',
                        fontWeight: 700, color: '#92400e', whiteSpace: 'nowrap', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {problems.map((d, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fffbeb' }}>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', fontWeight: 600, color: '#1e293b' }}>{d.rawName}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', fontFamily: 'monospace', color: d.isMatched ? '#16a34a' : '#dc2626' }}>{d.resolvedName}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', textAlign: 'center', color: d.isMatched ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{d.isMatched ? '✓' : '✗'}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', textAlign: 'center', fontWeight: 700 }}>{d.totalResignRecords}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', textAlign: 'center', color: '#16a34a', fontWeight: 700 }}>{d.inRange}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', textAlign: 'center', color: d.outOfRange > 0 ? '#dc2626' : C.text3, fontWeight: d.outOfRange > 0 ? 700 : 400 }}>{d.outOfRange || '—'}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', textAlign: 'center', color: d.dateParseFail > 0 ? '#dc2626' : C.text3, fontWeight: d.dateParseFail > 0 ? 700 : 400 }}>{d.dateParseFail || '—'}</td>
                      <td style={{ padding: '4px 10px', border: '1px solid #fde68a', fontFamily: 'monospace', fontSize: 10, color: '#78350f' }}>
                        {d.outOfRangeSamples.map((s, si) => <div key={si}>{s}</div>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {unmatchedLossDivisions.length > 0 && (
              <div style={{ marginTop: 6, padding: '6px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 11, color: '#7f1d1d' }}>
                <b>Unmatched names (not counted at all):</b> {unmatchedLossDivisions.join(', ')}<br/>
                Fix: add alias in <b>Cluster Config → Division Aliases</b>.
              </div>
            )}
          </details>
        );
      })()}

      {/* Month-wise Loss Summary — cluster-wise resignations only */}
      <div style={{ background: '#fff', border: `1.5px solid ${C.border}`, borderRadius: 10, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ background: '#7f1d1d', color: '#fff', fontWeight: 700, fontSize: 12,
          padding: '8px 16px', letterSpacing: 0.5 }}>
          Month-wise Loss Summary (Resignations by Cluster)
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fef2f2' }}>
                <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, color: '#7f1d1d',
                  border: `1px solid ${C.border}`, whiteSpace: 'nowrap', minWidth: 160 }}>Cluster</th>
                {months.map(mo => (
                  <th key={mo.key} style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 700,
                    color: '#7f1d1d', border: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{mo.label}</th>
                ))}
                <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 800,
                  color: '#fff', background: '#991b1b', border: `1px solid #991b1b`, whiteSpace: 'nowrap' }}>Total</th>
                <th style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 800,
                  color: '#fff', background: '#1e40af', border: `1px solid #1e40af`, whiteSpace: 'nowrap', minWidth: 100 }}>
                  Annualized<br/>Attrition %
                </th>
              </tr>
            </thead>
            <tbody>
              {clusters.map((cluster) => {
                const clusterMonthLoss = {};
                months.forEach(mo => {
                  clusterMonthLoss[mo.key] = cluster.divisions.reduce(
                    (s, d) => s + (d.lossMonthlyData?.[mo.key] || 0), 0
                  );
                });
                const clusterLossTotal = cluster.divisions.reduce((s, d) => s + (d.lossTotal || 0), 0);
                const clusterHC        = cluster.divisions.reduce((s, d) => s + (d.approvedHC || 0), 0);
                const clusterAttr      = clusterHC > 0
                  ? Math.round(clusterLossTotal * 12 / Math.max(1, nFYMonths) / clusterHC * 1000) / 10 : 0;
                return (
                  <React.Fragment key={cluster.clusterId}>
                    {/* Cluster header */}
                    <tr>
                      <td colSpan={3 + months.length}
                        style={{ padding: '6px 12px', fontWeight: 800, fontSize: 12, color: C.navy,
                          background: '#e8edf8', border: `1px solid ${C.border}`,
                          borderLeft: '4px solid #7f1d1d' }}>
                        {cluster.clusterHead}&nbsp;
                        <span style={{ fontWeight: 400, color: C.text3, fontSize: 11 }}>({cluster.clusterTitle})</span>
                      </td>
                    </tr>
                    {/* Division rows */}
                    {cluster.divisions.map(div => {
                      const divLossTotal = div.lossTotal || 0;
                      const divAttr = div.approvedHC > 0
                        ? Math.round(divLossTotal * 12 / Math.max(1, nFYMonths) / div.approvedHC * 1000) / 10 : 0;
                      return (
                        <tr key={div.divisionName} style={{ background: '#fff' }}>
                          <td style={{ padding: '6px 14px 6px 24px', color: C.text1,
                            border: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                            {div.divisionName}
                          </td>
                          {months.map(mo => {
                            const v = div.lossMonthlyData?.[mo.key] || 0;
                            return (
                              <td key={mo.key} style={{ padding: '6px 14px', textAlign: 'center',
                                fontWeight: v > 0 ? 700 : 400, color: v > 0 ? '#dc2626' : C.text3,
                                border: `1px solid ${C.border}` }}>
                                {v > 0
                                  ? <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
                                      onClick={() => openDrill(div.divisionName, mo)}>
                                      {v}
                                    </span>
                                  : '—'}
                              </td>
                            );
                          })}
                          <td style={{ padding: '6px 14px', textAlign: 'center', fontWeight: 700,
                            color: divLossTotal > 0 ? '#dc2626' : C.text3,
                            border: `1px solid #991b1b`, background: divLossTotal > 0 ? '#fff7f7' : '#fff' }}>
                            {divLossTotal || '—'}
                          </td>
                          <td style={{ padding: '6px 14px', textAlign: 'center', fontWeight: 800,
                            color: attrColor(divAttr), background: attrBg(divAttr),
                            border: `1px solid #1e40af` }}>
                            {divAttr > 0 ? `${divAttr}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Cluster subtotal */}
                    {cluster.divisions.length > 1 && (
                      <tr style={{ background: '#fef2f2', fontStyle: 'italic' }}>
                        <td style={{ padding: '6px 14px', fontWeight: 700, color: '#7f1d1d',
                          border: `1px solid ${C.border}` }}>
                          Sub-total ({cluster.clusterTitle})
                        </td>
                        {months.map(mo => {
                          const v = clusterMonthLoss[mo.key];
                          return (
                            <td key={mo.key} style={{ padding: '6px 14px', textAlign: 'center',
                              fontWeight: v > 0 ? 800 : 400, color: v > 0 ? '#dc2626' : C.text3,
                              border: `1px solid ${C.border}` }}>
                              {v || '—'}
                            </td>
                          );
                        })}
                        <td style={{ padding: '6px 14px', textAlign: 'center', fontWeight: 900,
                          color: clusterLossTotal > 0 ? '#dc2626' : C.text3, fontSize: 13,
                          border: `1px solid #991b1b`, background: '#fef2f2' }}>
                          {clusterLossTotal || '—'}
                        </td>
                        <td style={{ padding: '6px 14px', textAlign: 'center', fontWeight: 900,
                          color: attrColor(clusterAttr), background: attrBg(clusterAttr),
                          border: `1px solid #1e40af` }}>
                          {clusterAttr > 0 ? `${clusterAttr}%` : '—'}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {/* Grand Total row */}
              {(() => {
                const grandHC   = grandTotal.approvedHC || 0;
                const grandLoss = grandTotal.lossTotal || 0;
                const grandAttr = grandHC > 0
                  ? Math.round(grandLoss * 12 / Math.max(1, nFYMonths) / grandHC * 1000) / 10 : 0;
                return (
                  <tr style={{ background: '#fef2f2', fontWeight: 900 }}>
                    <td style={{ padding: '8px 14px', fontWeight: 900, color: '#7f1d1d',
                      border: `1px solid ${C.border}`, borderTop: '2px solid #991b1b' }}>
                      Grand Total
                    </td>
                    {months.map(mo => {
                      const v = grandTotal.lossMonthlyData?.[mo.key] || 0;
                      return (
                        <td key={mo.key} style={{ padding: '8px 14px', textAlign: 'center',
                          fontWeight: 900, color: v > 0 ? '#dc2626' : C.text3,
                          border: `1px solid ${C.border}`, borderTop: '2px solid #991b1b' }}>
                          {v || '—'}
                        </td>
                      );
                    })}
                    <td style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 900,
                      color: '#fff', fontSize: 14, background: '#991b1b',
                      border: `1px solid #991b1b`, borderTop: '2px solid #991b1b' }}>
                      {grandLoss}
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'center', fontWeight: 900,
                      color: '#fff', fontSize: 14, background: attrColor(grandAttr),
                      border: `1px solid #1e40af`, borderTop: '2px solid #991b1b' }}>
                      {grandAttr > 0 ? `${grandAttr}%` : '—'}
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drill-down modal — employees behind a Loss cell */}
      <Modal
        title={<span style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>{drill?.title}</span>}
        open={!!drill}
        onCancel={() => setDrill(null)}
        footer={null}
        width={840}
        styles={{ body: { padding: '16px 0 0' } }}
      >
        {drillLoading ? (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>
        ) : drill && (() => {
          const emps = drill.emps || [];
          const fmtDate = d => {
            if (!d) return '—';
            const dt = d instanceof Date ? d : new Date(d);
            if (isNaN(dt.getTime())) return '—';
            return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
          };
          return (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#7f1d1d' }}>
                      {['#', 'Code', 'Name', 'Designation', 'HQ', 'Zone', 'Join Date', 'Resign Date', 'Reason'].map(h => (
                        <th key={h} style={{ padding: '9px 10px', color: '#fff', fontWeight: 700,
                          textAlign: h === 'Name' || h === 'Designation' || h === 'Reason' ? 'left' : 'center',
                          borderRight: '1px solid rgba(255,255,255,0.15)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {emps.length === 0 ? (
                      <tr><td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                        No records found for this cell
                      </td></tr>
                    ) : emps.map((e, i) => (
                      <tr key={e.empCode || i} style={{
                        background: i % 2 === 0 ? '#fef2f2' : '#fff',
                        borderBottom: '1px solid #fecaca' }}>
                        <td style={{ padding: '8px 10px', textAlign: 'center', color: '#9ca3af', fontWeight: 600 }}>{i + 1}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#7f1d1d' }}>{e.empCode || '—'}</td>
                        <td style={{ padding: '8px 10px', fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}>{e.empName || '—'}</td>
                        <td style={{ padding: '8px 10px', color: '#4b5563' }}>{e.designation || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{e.hq || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>{e.zone || '—'}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', whiteSpace: 'nowrap' }}>{fmtDate(e.joiningDate)}</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>{fmtDate(e.resignDate)}</td>
                        <td style={{ padding: '8px 10px', color: '#6b7280', maxWidth: 200 }}>{e.reasonOfLeaving || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ padding: '10px 16px', background: '#fef2f2', borderTop: '1px solid #fecaca',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#7f1d1d', fontWeight: 700 }}>
                  Total: {emps.length} record{emps.length !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  Source: Attrition Records (Excel upload)
                </span>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* Pivot table (hidden) */}
      {false && <div style={{ overflowX: 'auto', borderRadius: 12, border: `1.5px solid ${C.border}` }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
          <thead>
            <tr>
              <TH rowSpan={2} style={{ minWidth: 150, textAlign: 'left' }}>Business Head / Division</TH>
              <TH rowSpan={2}>Approved HC<br/>FY 26-27</TH>
              {months.map(mo => <TH key={mo.key}>{mo.label}</TH>)}
              <TH style={{ background: '#1e40af' }}>YTD<br/>On-board</TH>
              <TH rowSpan={1} style={{ minWidth: 80 }}>Field<br/>Occupancy %</TH>
              <TH rowSpan={1}>Current HC</TH>
            </tr>
          </thead>
          <tbody>
            {clusters.map((cluster, ci) => {
              const rowBg = ci % 2 === 0 ? '#f0f4ff' : '#fff';
              return (
                <React.Fragment key={cluster.clusterId}>
                  {/* Cluster header row */}
                  <tr>
                    <td colSpan={3 + months.length + 2}
                      style={{ background: '#e8edf8', padding: '6px 12px', fontWeight: 800,
                        fontSize: 12, color: C.navy, border: '1px solid #cbd5e1', borderLeft: `4px solid ${C.navy}` }}>
                      {cluster.clusterHead} &nbsp;
                      <span style={{ fontWeight: 400, color: C.text3, fontSize: 11 }}>({cluster.clusterTitle})</span>
                    </td>
                  </tr>
                  {/* Division rows */}
                  {cluster.divisions.map(div => renderDivRow(div, true, { background: rowBg }))}
                  {/* Cluster subtotal */}
                  {cluster.divisions.length > 1 && (() => {
                    const subHC  = cluster.divisions.reduce((s, d) => s + d.approvedHC, 0);
                    const subR   = cluster.divisions.reduce((s, d) => s + d.ytdResign, 0);
                    const subO   = cluster.divisions.reduce((s, d) => s + d.ytdOnboarded, 0);
                    const subFil = cluster.divisions.reduce((s, d) => s + d.currentHeadcount, 0);
                    const subAnn = subHC > 0
                      ? Math.round((subR * 12 / Math.max(1, nFYMonths) / subHC) * 1000) / 10 : 0;
                    const subOcc = subHC > 0 ? Math.round(subFil / subHC * 1000) / 10 : 0;
                    return (
                      <tr style={{ background: '#e8edf8', fontStyle: 'italic' }}>
                        <TD align="left" style={{ fontWeight: 700, color: C.navy, paddingLeft: 8 }}>
                          Sub-total ({cluster.clusterTitle})
                        </TD>
                        <TD style={{ fontWeight: 700, color: C.navy }}>{subHC || '—'}</TD>
                        {months.map(mo => {
                          const o = cluster.divisions.reduce((s, d) => s + (d.monthlyData?.[mo.key]?.onboarded || 0), 0);
                          return (
                            <TD key={mo.key} style={{ color: o > 0 ? '#16a34a' : C.text3 }}>{o || '—'}</TD>
                          );
                        })}
                        <TD style={{ fontWeight: 800, color: '#16a34a' }}>{subO || '—'}</TD>
                        <TD style={{ fontWeight: 700, color: occColor(subOcc) }}>{subOcc > 0 ? `${subOcc}%` : '—'}</TD>
                        <TD style={{ fontWeight: 700, color: C.navy }}>{subFil || '—'}</TD>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}

            {/* Grand Total */}
            <tr style={{ background: '#f0fdf4', fontWeight: 900 }}>
              <TD align="left" style={{ fontWeight: 900, fontSize: 13, color: C.navy, borderTop: '2px solid #1B2D6B' }}>
                Grand Total
              </TD>
              <TD style={{ fontWeight: 900, color: C.navy, borderTop: '2px solid #1B2D6B' }}>{grandTotal.approvedHC}</TD>
              {months.map(mo => {
                const md = grandTotal.monthlyData?.[mo.key] || { onboarded: 0 };
                return (
                  <TD key={mo.key} style={{ color: md.onboarded > 0 ? '#16a34a' : C.text3, borderTop: '2px solid #1B2D6B' }}>{md.onboarded || '—'}</TD>
                );
              })}
              <TD style={{ fontWeight: 900, color: '#16a34a', fontSize: 13, borderTop: '2px solid #1B2D6B' }}>{grandTotal.ytdOnboarded}</TD>
              <TD style={{ fontWeight: 900, color: occColor(grandTotal.occupancyPct), fontSize: 13, borderTop: '2px solid #1B2D6B' }}>
                {grandTotal.occupancyPct}%
              </TD>
              <TD style={{ fontWeight: 900, color: C.navy, fontSize: 13, borderTop: '2px solid #1B2D6B' }}>{grandTotal.currentHeadcount}</TD>
            </tr>
          </tbody>
        </table>
      </div>}
    </div>
  );
}

// ══ Cluster Config Tab ══════════════════════════════════════════════
function ClusterConfigTab() {
  const [subTab, setSubTab]           = useState('clusters');
  const [clusters, setClusters]       = useState([]);
  const [mappings, setMappings]       = useState([]);
  const [clusterHeads, setClusterHeads] = useState([]);
  const [divHeads, setDivHeads]       = useState([]);
  const [aliases, setAliases]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modal, setModal]             = useState(null); // { type, data }
  const [form, setForm]               = useState({});
  const [saving, setSaving]           = useState(false);
  const [errMsg, setErrMsg]           = useState('');
  const [seeding, setSeeding]         = useState(false);
  const [showInactiveMappings, setShowInactiveMappings] = useState(false);
  const [searchText, setSearchText]   = useState('');

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, m, ch, dh, al] = await Promise.all([
        clusterService.getClusters(),
        clusterService.getMappings(),
        clusterService.getClusterHeads(),
        clusterService.getDivisionHeads(),
        clusterService.getAliases(),
      ]);
      setClusters(c); setMappings(m); setClusterHeads(ch); setDivHeads(dh); setAliases(al);
    } catch (e) { setErrMsg(e?.message || 'Load failed'); }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const openAdd  = (type) => { setModal({ type, data: null }); setForm({}); setErrMsg(''); };
  const openEdit = (type, item) => { setModal({ type, data: item }); setForm({ ...item }); setErrMsg(''); };
  const closeModal = () => { setModal(null); setForm({}); setErrMsg(''); };

  const handleSave = async () => {
    setSaving(true); setErrMsg('');
    try {
      const { type, data } = modal;
      if (type === 'cluster') {
        data ? await clusterService.updateCluster(data.id, form) : await clusterService.createCluster(form);
      } else if (type === 'mapping') {
        data ? await clusterService.updateMapping(data.id, form) : await clusterService.createMapping(form);
      } else if (type === 'clusterHead') {
        data ? await clusterService.updateClusterHead(data.id, form) : await clusterService.createClusterHead(form);
      } else if (type === 'divHead') {
        data ? await clusterService.updateDivisionHead(data.id, form) : await clusterService.createDivisionHead(form);
      } else if (type === 'alias') {
        data ? await clusterService.updateAlias(data.id, form) : await clusterService.createAlias(form);
      }
      closeModal(); loadAll();
    } catch (e) {
      setErrMsg(e?.response?.data?.message || e?.message || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      if (type === 'cluster')      await clusterService.deleteCluster(id);
      else if (type === 'mapping')     await clusterService.deleteMapping(id);
      else if (type === 'clusterHead') await clusterService.deleteClusterHead(id);
      else if (type === 'divHead')     await clusterService.deleteDivisionHead(id);
      else if (type === 'alias')       await clusterService.deleteAlias(id);
      loadAll();
    } catch (e) { setErrMsg(e?.response?.data?.message || e?.message || 'Delete failed'); }
  };

  const handleToggleActive = async (type, rec) => {
    try {
      const payload = { isActive: !rec.isActive };
      if (type === 'cluster')      await clusterService.updateCluster(rec.id, payload);
      else if (type === 'mapping')     await clusterService.updateMapping(rec.id, payload);
      else if (type === 'clusterHead') await clusterService.updateClusterHead(rec.id, payload);
      else if (type === 'divHead')     await clusterService.updateDivisionHead(rec.id, payload);
      else if (type === 'alias')       await clusterService.updateAlias(rec.id, payload);
      loadAll();
    } catch (e) { setErrMsg(e?.response?.data?.message || e?.message || 'Toggle failed'); }
  };

  // Reusable active toggle column renderer
  const activeCol = (type) => ({
    title: 'Active',
    dataIndex: 'isActive',
    width: 90,
    align: 'center',
    render: (v, rec) => (
      <Switch
        size="small"
        checked={!!v}
        checkedChildren="Active"
        unCheckedChildren="Inactive"
        onChange={() => handleToggleActive(type, rec)}
        style={{ minWidth: 72 }}
      />
    ),
  });

  const F = ({ label, name, type = 'text', options }) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.text2, display: 'block', marginBottom: 4 }}>{label}</label>
      {options ? (
        <select value={form[name] || ''} onChange={e => setForm(p => ({ ...p, [name]: e.target.value }))}
          style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }}>
          <option value="">-- select --</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={form[name] || ''} placeholder={label}
          onChange={e => setForm(p => ({ ...p, [name]: type === 'number' ? Number(e.target.value) : e.target.value }))}
          style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, boxSizing: 'border-box' }} />
      )}
    </div>
  );

  const clusterOptions = clusters.map(c => ({ value: c.id, label: c.title }));

  const subTabs = [
    { key: 'clusters',    label: 'Clusters' },
    { key: 'mappings',    label: 'Division Mapping' },
    { key: 'clusterHeads', label: 'Cluster Heads' },
    { key: 'divHeads',    label: 'Division Heads' },
    { key: 'aliases',     label: 'Division Aliases' },
  ];

  const btnStyle = (active) => ({
    padding: '6px 16px', borderRadius: 6, border: `1px solid ${C.border}`,
    cursor: 'pointer', fontWeight: 600, fontSize: 12,
    background: active ? C.navy : '#fff', color: active ? '#fff' : C.text2,
    marginRight: 6,
  });

  const actionBtn = (label, onClick, color = C.navy) => (
    <button onClick={onClick} style={{ padding: '3px 10px', borderRadius: 5, border: `1px solid ${color}44`,
      background: `${color}11`, color, cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4 }}>
      {label}
    </button>
  );

  return (
    <div>
      {errMsg && <Alert message={errMsg} type="error" closable style={{ marginBottom: 12 }} onClose={() => setErrMsg('')} />}

      {/* Sub-tab bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>{subTabs.map(t => <button key={t.key} style={btnStyle(subTab === t.key)} onClick={() => { setSubTab(t.key); setSearchText(''); }}>{t.label}</button>)}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="small" loading={seeding}
            style={{ borderColor: '#16a34a', color: '#16a34a', background: '#f0fdf4' }}
            onClick={async () => {
              if (!window.confirm('Seed cluster master data from the employee mapping sheet? This will insert 15 clusters, 29 division mappings, 5 cluster heads, and 28 division heads. Skip if data already exists.')) return;
              setSeeding(true); setErrMsg('');
              try {
                const res = await clusterService.seedMasterData();
                if (res.skipped) { setErrMsg(res.message); }
                else { loadAll(); }
              } catch (e) { setErrMsg(e?.response?.data?.message || e?.message || 'Seed failed'); }
              setSeeding(false);
            }}>
            Seed Master Data
          </Button>
          <Button type="primary" size="small" style={{ background: C.navy }}
            onClick={() => openAdd(subTab === 'clusters' ? 'cluster' : subTab === 'mappings' ? 'mapping' : subTab === 'clusterHeads' ? 'clusterHead' : subTab === 'aliases' ? 'alias' : 'divHead')}>
            + Add
          </Button>
        </div>
      </div>

      <Spin spinning={loading}>
        {/* Clusters table */}
        {subTab === 'clusters' && (() => {
          const q = searchText.trim().toLowerCase();
          const rows = q ? clusters.filter(c => (c.title || '').toLowerCase().includes(q)) : clusters;
          return (
            <>
              <div style={{ marginBottom: 10 }}>
                <Input prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search cluster name…" allowClear value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 260, borderRadius: 7 }} size="small" />
              </div>
              <Table size="small"
                dataSource={[...rows].sort((a, b) => {
                  const sa = a.sequenceNo ?? 9999, sb = b.sequenceNo ?? 9999;
                  return sa !== sb ? sa - sb : (a.title || '').localeCompare(b.title || '');
                })}
                rowKey="id"
                pagination={{ pageSize: 20, showTotal: t => `${t} rows` }}
                style={{ background: '#fff', borderRadius: 10 }}
                columns={[
                  { title: 'Seq', dataIndex: 'sequenceNo', width: 60, align: 'center',
                    render: v => <span style={{ fontWeight: 700, color: C.navy }}>{v ?? '—'}</span> },
                  { title: 'ID',    dataIndex: 'id',    width: 60 },
                  { title: 'Title', dataIndex: 'title', render: v => <b>{v}</b> },
                  activeCol('cluster'),
                  { title: 'Actions', width: 110, render: (_, r) => <>{actionBtn('Edit', () => openEdit('cluster', r), C.blue)}{actionBtn('Del', () => handleDelete('cluster', r.id), '#dc2626')}</> },
                ]} />
            </>
          );
        })()}

        {/* Mappings table */}
        {subTab === 'mappings' && (() => {
          const activeMappings   = mappings.filter(m => m.isActive);
          const inactiveMappings = mappings.filter(m => !m.isActive);
          const baseMappings     = showInactiveMappings ? mappings : activeMappings;
          const q = searchText.trim().toLowerCase();
          const displayMappings  = q
            ? baseMappings.filter(m => {
                const divMatch     = (m.divisionName || '').toLowerCase().includes(q);
                const clusterTitle = clusters.find(c => c.id === +m.clusterId)?.title || '';
                return divMatch || clusterTitle.toLowerCase().includes(q);
              })
            : baseMappings;
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                <Input
                  prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search division or cluster…"
                  allowClear
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 260, borderRadius: 7 }}
                  size="small"
                />
                <span style={{ fontSize: 12, color: C.text3 }}>
                  {q
                    ? <><b>{displayMappings.length}</b> match{displayMappings.length !== 1 ? 'es' : ''} of {baseMappings.length}</>
                    : <><b>{activeMappings.length}</b> active{inactiveMappings.length > 0 && ` · ${inactiveMappings.length} inactive`}</>
                  }
                </span>
                {inactiveMappings.length > 0 && (
                  <Switch
                    size="small"
                    checked={showInactiveMappings}
                    onChange={setShowInactiveMappings}
                    checkedChildren="Show All"
                    unCheckedChildren="Active Only"
                  />
                )}
              </div>
              <Table size="small" dataSource={displayMappings} rowKey="id"
                pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `${t} rows` }}
                style={{ background: '#fff', borderRadius: 10 }}
                onRow={r => ({ style: !r.isActive ? { opacity: 0.45, background: '#f5f5f5' } : {} })}
                columns={[
                  { title: 'ID',           dataIndex: 'id',           width: 60 },
                  { title: 'Division',     dataIndex: 'divisionName',
                    render: (v, r) => {
                      if (!q) return <b style={{ color: r.isActive ? 'inherit' : '#aaa' }}>{v}</b>;
                      const idx = v.toLowerCase().indexOf(q);
                      if (idx < 0) return <b style={{ color: r.isActive ? 'inherit' : '#aaa' }}>{v}</b>;
                      return (
                        <b style={{ color: r.isActive ? 'inherit' : '#aaa' }}>
                          {v.slice(0, idx)}
                          <span style={{ background: '#fef08a' }}>{v.slice(idx, idx + q.length)}</span>
                          {v.slice(idx + q.length)}
                        </b>
                      );
                    }},
                  { title: 'Cluster', dataIndex: 'clusterId',
                    render: v => {
                      const title = clusters.find(c => c.id === +v)?.title || String(v);
                      if (!q) return title;
                      const idx = title.toLowerCase().indexOf(q);
                      if (idx < 0) return title;
                      return (
                        <>
                          {title.slice(0, idx)}
                          <span style={{ background: '#fef08a' }}>{title.slice(idx, idx + q.length)}</span>
                          {title.slice(idx + q.length)}
                        </>
                      );
                    }},
                  activeCol('mapping'),
                  { title: 'Actions', width: 110, render: (_, r) => <>{actionBtn('Edit', () => openEdit('mapping', r), C.blue)}{actionBtn('Del', () => handleDelete('mapping', r.id), '#dc2626')}</> },
                ]} />
            </>
          );
        })()}

        {/* Cluster heads table */}
        {subTab === 'clusterHeads' && (() => {
          const q = searchText.trim().toLowerCase();
          const rows = q ? clusterHeads.filter(h =>
            (h.clusterHeadEmpName || '').toLowerCase().includes(q) ||
            (h.clusterHeadEmpCode || '').toLowerCase().includes(q) ||
            (clusters.find(c => c.id === +h.clusterId)?.title || '').toLowerCase().includes(q)
          ) : clusterHeads;
          return (
            <>
              <div style={{ marginBottom: 10 }}>
                <Input prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search name, code, or cluster…" allowClear value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 280, borderRadius: 7 }} size="small" />
              </div>
              <Table size="small" dataSource={rows} rowKey="id"
                pagination={{ pageSize: 20, showTotal: t => `${t} rows` }}
                style={{ background: '#fff', borderRadius: 10 }}
                columns={[
                  { title: 'ID',       dataIndex: 'id',                  width: 60 },
                  { title: 'Name',     dataIndex: 'clusterHeadEmpName',  render: v => <b>{v}</b> },
                  { title: 'Emp Code', dataIndex: 'clusterHeadEmpCode' },
                  { title: 'Email',    dataIndex: 'clusterHeadEmpEmail' },
                  { title: 'Cluster',  dataIndex: 'clusterId', render: v => clusters.find(c => c.id === +v)?.title || v },
                  activeCol('clusterHead'),
                  { title: 'Actions', width: 110, render: (_, r) => <>{actionBtn('Edit', () => openEdit('clusterHead', r), C.blue)}{actionBtn('Del', () => handleDelete('clusterHead', r.id), '#dc2626')}</> },
                ]} />
            </>
          );
        })()}

        {/* Division heads table */}
        {subTab === 'divHeads' && (() => {
          const q = searchText.trim().toLowerCase();
          const rows = q ? divHeads.filter(h =>
            (h.divisionEmpName || '').toLowerCase().includes(q) ||
            (h.divisionEmpCode || '').toLowerCase().includes(q) ||
            (clusters.find(c => c.id === +h.clusterId)?.title || '').toLowerCase().includes(q)
          ) : divHeads;
          return (
            <>
              <div style={{ marginBottom: 10 }}>
                <Input prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search name, code, or cluster…" allowClear value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 280, borderRadius: 7 }} size="small" />
              </div>
              <Table size="small" dataSource={rows} rowKey="id"
                pagination={{ pageSize: 20, showTotal: t => `${t} rows` }}
                style={{ background: '#fff', borderRadius: 10 }}
                columns={[
                  { title: 'ID',       dataIndex: 'id',              width: 60 },
                  { title: 'Name',     dataIndex: 'divisionEmpName', render: v => <b>{v}</b> },
                  { title: 'Emp Code', dataIndex: 'divisionEmpCode' },
                  { title: 'Email',    dataIndex: 'divisionEmpEmail' },
                  { title: 'Cluster',  dataIndex: 'clusterId', render: v => clusters.find(c => c.id === +v)?.title || v },
                  activeCol('divHead'),
                  { title: 'Actions', width: 110, render: (_, r) => <>{actionBtn('Edit', () => openEdit('divHead', r), C.blue)}{actionBtn('Del', () => handleDelete('divHead', r.id), '#dc2626')}</> },
                ]} />
            </>
          );
        })()}

        {/* Division Aliases table */}
        {subTab === 'aliases' && (() => {
          const q = searchText.trim().toLowerCase();
          const rows = q ? aliases.filter(a =>
            (a.alias || '').toLowerCase().includes(q) ||
            (a.canonicalName || '').toLowerCase().includes(q)
          ) : aliases;
          return (
            <>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 14px', marginBottom: 10, fontSize: 12, color: '#92400e' }}>
                Map alternate division names (as they appear in attrition records) to their canonical name in Division Mapping. Case-insensitive.
              </div>
              <div style={{ marginBottom: 10 }}>
                <Input prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                  placeholder="Search alias or canonical name…" allowClear value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{ width: 280, borderRadius: 7 }} size="small" />
              </div>
              <Table size="small" dataSource={rows} rowKey="id"
                pagination={{ pageSize: 30, showTotal: t => `${t} rows` }}
                style={{ background: '#fff', borderRadius: 10 }}
                columns={[
                  { title: 'ID',             dataIndex: 'id',            width: 60 },
                  { title: 'Alias (in records)', dataIndex: 'alias',     render: v => <Tag color="orange">{v}</Tag> },
                  { title: 'Canonical Name', dataIndex: 'canonicalName', render: v => <b>{v}</b> },
                  activeCol('alias'),
                  { title: 'Actions', width: 110, render: (_, r) => <>{actionBtn('Edit', () => openEdit('alias', r), C.blue)}{actionBtn('Del', () => handleDelete('alias', r.id), '#dc2626')}</> },
                ]} />
            </>
          );
        })()}
      </Spin>

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: '#00000066', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, maxHeight: '80vh',
            overflowY: 'auto', boxShadow: '0 20px 60px #00000033' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <span style={{ fontWeight: 800, fontSize: 16, color: C.navy }}>
                {modal.data ? 'Edit' : 'Add'} {modal.type === 'cluster' ? 'Cluster' : modal.type === 'mapping' ? 'Division Mapping' : modal.type === 'clusterHead' ? 'Cluster Head' : modal.type === 'alias' ? 'Division Alias' : 'Division Head'}
              </span>
              <button onClick={closeModal} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: C.text3 }}>×</button>
            </div>

            {errMsg && <Alert message={errMsg} type="error" style={{ marginBottom: 12 }} closable onClose={() => setErrMsg('')} />}

            {modal.type === 'cluster' && <>
              <F label="Seq No" name="sequenceNo" type="number" />
              <F label="Cluster Title *" name="title" />
            </>}

            {modal.type === 'mapping' && <>
              <F label="Division Name *" name="divisionName" />
              <F label="Cluster *" name="clusterId" type="number" options={clusterOptions} />
            </>}

            {modal.type === 'clusterHead' && <>
              <F label="Name *" name="clusterHeadEmpName" />
              <F label="Emp Code" name="clusterHeadEmpCode" />
              <F label="Email" name="clusterHeadEmpEmail" type="email" />
              <F label="Cluster *" name="clusterId" type="number" options={clusterOptions} />
            </>}

            {modal.type === 'divHead' && <>
              <F label="Name *" name="divisionEmpName" />
              <F label="Emp Code" name="divisionEmpCode" />
              <F label="Email" name="divisionEmpEmail" type="email" />
              <F label="Cluster *" name="clusterId" type="number" options={clusterOptions} />
            </>}

            {modal.type === 'alias' && <>
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', marginBottom: 12, fontSize: 11, color: '#92400e' }}>
                Enter the variant name exactly as it appears in attrition records (case-insensitive match).
              </div>
              <F label="Alias (variant name in records) *" name="alias" />
              <F label="Canonical Name (in Division Mapping) *" name="canonicalName" />
            </>}

            {modal.data && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.text2, display: 'block', marginBottom: 4 }}>Active</label>
                <label style={{ cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={!!form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
                  {' '}Active
                </label>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <Button onClick={closeModal}>Cancel</Button>
              <Button type="primary" loading={saving} onClick={handleSave} style={{ background: C.navy }}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TA Tracker Tab
// ─────────────────────────────────────────────────────────────────────────────
const TA_DESIG_MAP = {
  // mt: 'MT',
  be: 'BE', tbm: 'BE', 'sr be': 'BE', bdm: 'BE', tm: 'BE',
  abm: 'ABM', 'sr abm': 'ABM',
  rbm: 'RBM', 'sr rbm': 'RBM',
  sm: 'SM', 'sr sm': 'SM',
  dsm: 'DSM', 'sr dsm': 'DSM',
};

function TaTrackerTab() {
  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [records,      setRecords]      = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [search,       setSearch]       = useState('');
  const [filterAV,     setFilterAV]     = useState('');
  const [filterDiv,    setFilterDiv]    = useState('');
  const [filterGrade,  setFilterGrade]  = useState('');
  const pageSize = 50;
  const fileRef  = useRef();

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const d = await attritionOccupancyService.getTaStats();
      setStats(d);
    } catch { /* ignore */ }
    finally { setStatsLoading(false); }
  }, []);

  const loadRecords = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const d = await attritionOccupancyService.getTaRecords({
        page: pg, limit: pageSize,
        search: search || undefined,
        activeVacant: filterAV   || undefined,
        division:     filterDiv  || undefined,
        grade:        filterGrade || undefined,
      });
      setRecords(d.data || []);
      setTotal(d.total || 0);
      setPage(pg);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [search, filterAV, filterDiv, filterGrade]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadRecords(1); }, [loadRecords]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    try {
      const res = await attritionOccupancyService.uploadTaExcel(file);
      antMsg.success(`Uploaded ${res.inserted} records from "${res.sheet}"`);
      loadStats();
      loadRecords(1);
    } catch (err) {
      antMsg.error(err?.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await attritionOccupancyService.downloadTaSampleFormat();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'TA_Tracker_Upload_Format.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { antMsg.error('Download failed'); }
  };

  const avColor = (av) => {
    const l = (av || '').toLowerCase();
    if (l === 'active') return { color: '#15803d', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 700 };
    if (l === 'vacant') return { color: '#b91c1c', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 4, padding: '1px 8px', fontSize: 11, fontWeight: 700 };
    return { color: '#475569', fontSize: 11 };
  };

  const divOptions = stats ? Object.keys(stats.byDivision).sort() : [];
  const gradeOptions = stats ? Object.keys(stats.byGrade).sort() : [];

  const columns = [
    { title: 'Active/Vacant', dataIndex: 'activeVacant', width: 110, fixed: 'left',
      render: v => <span style={avColor(v)}>{v || '—'}</span> },
    { title: 'Grade / Designation', dataIndex: 'grade', width: 130,
      render: v => v ? <b style={{ color: C.navy }}>{v}</b> : '—' },
    { title: 'Division',    dataIndex: 'division',    width: 120 },
    { title: 'Emp Code',    dataIndex: 'empCode',     width: 100, render: v => v || '—' },
    { title: 'Emp Name',    dataIndex: 'empName',     width: 160, render: v => v || '—' },
    { title: 'HQ',          dataIndex: 'hq',          width: 120 },
    { title: 'State',       dataIndex: 'state',       width: 110 },
    { title: 'Zone',        dataIndex: 'zone',        width: 90  },
    { title: 'Status',      dataIndex: 'status',      width: 120 },
    { title: 'Ageing Days', dataIndex: 'ageingDays',  width: 110,
      render: v => {
        if (v == null) return '—';
        const color = v > 90 ? '#b91c1c' : v > 60 ? '#d97706' : v > 30 ? '#ca8a04' : '#15803d';
        return <span style={{ fontWeight: 700, color }}>{v}</span>;
      }},
    { title: 'Ageing Band',     dataIndex: 'ageingBand',     width: 110 },
    { title: 'Vacancy Active',  dataIndex: 'vacancyActiveDate',  width: 120 },
    { title: 'CV Received',     dataIndex: 'cvReceivedDate',     width: 110 },
    { title: 'Interview Date',  dataIndex: 'interviewDate',      width: 115 },
    { title: 'Selected Date',   dataIndex: 'selectedDate',       width: 110 },
    { title: 'Offer Date',      dataIndex: 'offerDate',          width: 105 },
    { title: 'New DOJ',         dataIndex: 'newJoiningDate',     width: 105 },
    { title: 'HR Working Status', dataIndex: 'hrWorkingStatus',  width: 140 },
    { title: 'Total Candidates', dataIndex: 'totalCandidates',   width: 130 },
    { title: 'Interviewed',      dataIndex: 'candidatesInterviewed', width: 105 },
    { title: 'Position ID',     dataIndex: 'positionId',         width: 105 },
    { title: 'Vacant Code',     dataIndex: 'vacantCode',         width: 105 },
    { title: 'Remarks',         dataIndex: 'remarks',            width: 180,
      render: v => v ? <span title={v}>{v.length > 40 ? v.slice(0, 40) + '…' : v}</span> : '—' },
    { title: 'HRBP',            dataIndex: 'hrbp',               width: 130 },
  ];

  const statsCards = stats ? [
    { label: 'Total Records',     value: stats.total,  risk: 'neutral', badge: 'ALL' },
    { label: 'Active Headcount',  value: stats.active, risk: 'good',    badge: 'ACTIVE' },
    { label: 'Vacant Positions',  value: stats.vacant, risk: stats.vacant > 0 ? 'warning' : 'good', badge: 'VACANT' },
    {
      label: 'Occupancy %',
      value: stats.total > 0 ? `${Math.round(stats.active / stats.total * 100)}%` : '—',
      risk:  stats.total > 0 && (stats.active / stats.total) < 0.85 ? 'warning' : 'good',
      badge: 'OCC',
    },
  ] : [];

  const byDivRows = stats ? Object.entries(stats.byDivision)
    .sort((a, b) => b[1].total - a[1].total)
    .map(([div, d]) => ({ div, ...d, occ: d.total > 0 ? Math.round(d.active / d.total * 100) : 0 }))
    : [];

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.navy }}>TA Tracker</div>
          <div style={{ fontSize: 12, color: C.text3, marginTop: 2 }}>
            Talent Acquisition pipeline — sanctioned strength source
            {stats?.lastUploadAt && <span style={{ marginLeft: 8, color: '#94a3b8' }}>Last upload: {new Date(stats.lastUploadAt).toLocaleDateString('en-IN')}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button icon={<DownloadOutlined />} onClick={handleDownloadSample} style={{ borderRadius: 8 }}>
            Sample Format
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleUpload} />
          <Button
            type="primary" icon={<UploadOutlined />}
            loading={uploading}
            onClick={() => fileRef.current?.click()}
            style={{ borderRadius: 8, background: C.navy }}
          >
            Upload Excel
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      <Spin spinning={statsLoading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {statsCards.map(c => (
            <Col key={c.label} xs={12} sm={6}>
              <AnalyticCard {...c} />
            </Col>
          ))}
        </Row>

        {/* Unmapped divisions warning */}
        {stats?.unmappedDivisions?.length > 0 && (
          <div style={{
            background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 10,
            padding: '12px 16px', marginBottom: 20,
          }}>
            <div style={{ fontWeight: 700, color: '#92400e', fontSize: 13, marginBottom: 6 }}>
              ⚠ {stats.unmappedTotal} rows in {stats.unmappedDivisions.length} division(s) are NOT mapped to any cluster — excluded from Dashboard &amp; Matrix
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {stats.unmappedDivisions.map(d => (
                <span key={d.division} style={{ fontSize: 12, color: '#78350f', fontWeight: 600 }}>
                  {d.division} <span style={{ color: '#b45309' }}>({d.count})</span>
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>
              Go to <b>Cluster Config → Division Mapping</b> to add these divisions to a cluster, or add a <b>Division Alias</b> if the name differs.
            </div>
          </div>
        )}
        {stats && !stats.unmappedDivisions?.length && (
          <div style={{ fontSize: 12, color: '#15803d', marginBottom: 16 }}>
            ✓ All {stats.total} ta_tracker rows are mapped to active clusters — counts are consistent across all views.
          </div>
        )}
      </Spin>

      {/* Division breakdown mini-table */}
      {byDivRows.length > 0 && (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, color: C.navy, fontSize: 14, marginBottom: 12 }}>Division-wise Summary</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.navy, color: '#fff' }}>
                  {['Division', 'Total', 'Active', 'Vacant', 'Occupancy %'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Division' ? 'left' : 'center', fontWeight: 700, fontSize: 11, letterSpacing: '0.3px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byDivRows.map((r, i) => (
                  <tr key={r.div} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff', borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '7px 12px', fontWeight: 600, color: C.text1 }}>{r.div}</td>
                    <td style={{ padding: '7px 12px', textAlign: 'center' }}>{r.total}</td>
                    <td style={{ padding: '7px 12px', textAlign: 'center', color: '#15803d', fontWeight: 700 }}>{r.active}</td>
                    <td style={{ padding: '7px 12px', textAlign: 'center', color: r.vacant > 0 ? '#b91c1c' : '#64748b', fontWeight: r.vacant > 0 ? 700 : 400 }}>{r.vacant}</td>
                    <td style={{ padding: '7px 12px', textAlign: 'center' }}>
                      <span style={{
                        fontWeight: 700,
                        color: r.occ >= 90 ? '#15803d' : r.occ >= 75 ? '#d97706' : '#b91c1c',
                      }}>{r.occ}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Filters + records table */}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Input
            placeholder="Search name / code / division / HQ…"
            prefix={<SearchOutlined />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onPressEnter={() => loadRecords(1)}
            allowClear
            style={{ width: 280, borderRadius: 8 }}
          />
          <Select
            placeholder="Active / Vacant"
            value={filterAV || undefined}
            onChange={v => setFilterAV(v || '')}
            allowClear style={{ width: 140 }}
          >
            <Option value="Active">Active</Option>
            <Option value="Vacant">Vacant</Option>
          </Select>
          <Select
            placeholder="Division"
            value={filterDiv || undefined}
            onChange={v => setFilterDiv(v || '')}
            allowClear showSearch style={{ width: 160 }}
          >
            {divOptions.map(d => <Option key={d} value={d}>{d}</Option>)}
          </Select>
          <Select
            placeholder="Grade / Designation"
            value={filterGrade || undefined}
            onChange={v => setFilterGrade(v || '')}
            allowClear showSearch style={{ width: 120 }}
          >
            {gradeOptions.map(g => <Option key={g} value={g}>{g}</Option>)}
          </Select>
          <Button type="primary" onClick={() => loadRecords(1)} style={{ borderRadius: 8, background: C.navy }}>
            Search
          </Button>
        </div>

        <Spin spinning={loading}>
          <Table
            dataSource={records}
            columns={columns}
            rowKey="id"
            size="small"
            scroll={{ x: 2400 }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
              showTotal: (t) => `${t} records`,
              onChange: (p) => loadRecords(p),
            }}
            locale={{ emptyText: <Empty description="No TA Tracker data — upload an Excel file to get started" /> }}
            rowClassName={(_, i) => i % 2 === 0 ? '' : 'ant-table-row-alt'}
          />
        </Spin>
      </div>
    </div>
  );
}
